/**
 * DreamMee – App Controller
 * Dynamic-step wizard: Design → [per-element steps] → Scene → Product+Checkout
 * 
 * Self-contained: generates all DOM elements into a container div.
 * Works both locally (index.html) and embedded in Shopify (section template).
 * 
 * Usage:
 *   <div id="dreammee-app"></div>
 *   <script> new DreamMeeApp('dreammee-app'); </script>
 */

class DreamMeeApp {
  static SHIRT_SIZES = ['S', 'M', 'L', 'XL', 'XXL', '3XL'];
  static SHIRT_COLORS = [
    { hex: '#0A0A0A', name: 'Black' },
    { hex: '#E79AF6', name: 'Pink' },
    { hex: '#1AB3FF', name: 'Blue' },
    { hex: '#F5DCC3', name: 'Cream' },
    { hex: '#FFFFFF', name: 'White' }
  ];

  getSlotPrefix(slot) {
    if (slot === 'character_1') return 'char';
    if (slot === 'car_1') return 'car';
    return slot;
  }

  get characterCreator() {
    return this.slotState?.character_1?.creator || null;
  }

  get carCreator() {
    return this.slotState?.car_1?.creator || null;
  }

  constructor(containerId = 'dreammee-app') {
    this.container = document.getElementById(containerId);
    if (!this.container) {
      console.error(`[DreamMee] Container #${containerId} not found`);
      return;
    }

    this.currentStepIdx = 0; // index into this.steps[]
    this.state = {
      orderMode: null, // 'convention' | 'shipping'
      product: {
        type: 'tshirt',
        size: 'S',
        color: '#0A0A0A',
        colorName: 'Black'
      },
      designId: null,
      sceneRecipe: null
    };

    // Convention cart — separate from Shopify cart, persisted in sessionStorage
    this.conventionCart = this._loadCart();

    // Per-slot element state keyed by slot name (e.g. 'character_1', 'car_1')
    // Each: { mode, premadeCharId, premadeCarModelId, premadeCarVariantId, creator, recipe, galleryRendered }
    this.slotState = {};

    Object.defineProperties(this.state, {
      characterMode: {
        get: () => this.slotState['character_1']?.mode,
        set: (val) => {
          if (this.slotState['character_1']) this.slotState['character_1'].mode = val;
        },
        configurable: true,
        enumerable: true
      },
      carMode: {
        get: () => this.slotState['car_1']?.mode,
        set: (val) => {
          if (this.slotState['car_1']) this.slotState['car_1'].mode = val;
        },
        configurable: true,
        enumerable: true
      },
      characterRecipe: {
        get: () => this.slotState['character_1']?.recipe,
        set: (val) => {
          if (this.slotState['character_1']) this.slotState['character_1'].recipe = val;
        },
        configurable: true,
        enumerable: true
      },
      carRecipe: {
        get: () => this.slotState['car_1']?.recipe,
        set: (val) => {
          if (this.slotState['car_1']) this.slotState['car_1'].recipe = val;
        },
        configurable: true,
        enumerable: true
      }
    });

    // Dynamic step list — rebuilt when design changes
    // Each: { id, type: 'design'|'character'|'car'|'product'|'scene'|'checkout', label, slot? }
    this.steps = [
      { id: 'mode-select', type: 'mode-select', label: 'Order Type', panelId: 'step-mode-select' },
      { id: 'design', type: 'design', label: 'Design', panelId: 'step-1' },
      { id: 'product', type: 'product', label: 'Size & Color', panelId: 'step-product' },
      { id: 'scene', type: 'scene', label: 'Put It Together', panelId: 'step-4' },
      { id: 'checkout', type: 'checkout', label: 'Complete Order', panelId: 'step-5' }
    ];

    // Sub-modules
    this.sceneComposer = null;

    this.renderShell();
    this.init();
  }

  // ==================== RENDER SHELL ====================
  // Design, scene, and checkout panels are rendered statically.
  // Element step panels (character/car) are injected dynamically by rebuildSteps().

  renderShell() {
    this.container.innerHTML = `
      ${this.renderHeader()}
      <main class="app-main" id="app-main">
        ${this.renderModeSelection()}
        ${this.renderStep1()}
        ${this.renderProductStep()}
        ${this.renderStep4()}
        ${this.renderStep5()}
      </main>
      ${this.renderPostAddPopup()}
      ${this.renderAddCharPopup()}
      ${this.renderCartViewer()}
    `;
  }

  renderHeader() {
    return `
      <header class="app-header" id="app-header">
        <button class="app-header__home" id="btn-home" title="Go to Design step"></button>
        <div class="app-header__brand">
          <span class="app-header__logo">DreamMee</span>
          <span class="app-header__subtitle">Anime Expo 2026 Con Exclusive</span>
        </div>
        <nav class="app-stepper" id="app-stepper"></nav>
      </header>`;
  }

  /** Rebuild the step list from the current design's elements, inject step panels, rebuild stepper nav */
  rebuildSteps() {
    const design = this.getSelectedDesign();
    const elements = design ? getDesignElements(design) : [];

    // Separate characters and cars
    const allCharacters = elements.filter(el => el.type === 'character');
    const allCars = elements.filter(el => el.type === 'car');

    // Progressive element building: only include the first character and first car initially.
    // Additional characters/cars are added via the "Add Another" popup.
    const firstChar = allCharacters.length > 0 ? [allCharacters[0]] : [];
    this.pendingCharacterElements = allCharacters.slice(1);
    this._allCharacterElements = [...allCharacters];

    const firstCar = allCars.length > 0 ? [allCars[0]] : [];
    this.pendingCarElements = allCars.slice(1);
    this._allCarElements = [...allCars];

    // Active elements = first character + first car
    const sortedElements = [
      ...firstChar,
      ...firstCar
    ];

    // Reset scene recipe and navigation history as design changed
    this.state.sceneRecipe = null;
    this._maxStepReached = 0;

    // Build dynamic step list: Design → Product (Size & Color) → [element steps in order] → Put It Together → Complete Order
    this.steps = [
      { id: 'mode-select', type: 'mode-select', label: 'Order Type', panelId: 'step-mode-select' },
      { id: 'design', type: 'design', label: 'Design', panelId: 'step-1' },
      { id: 'product', type: 'product', label: 'Size & Color', panelId: 'step-product' }
    ];

    let charCount = 0;
    let carCount = 0;
    // Total characters currently in steps (starts with 1 if any exist; label updates as more are added)
    const hasMultipleChars = allCharacters.length > 1;
    const totalCars = allCars.length;

    for (const el of sortedElements) {
      let panelId = '';
      let label = '';
      if (el.type === 'character') {
        charCount++;
        panelId = charCount === 1 ? 'step-2' : `step-2-${charCount}`;
        label = hasMultipleChars ? `Character #${charCount}` : 'Character';
      } else if (el.type === 'car') {
        carCount++;
        panelId = carCount === 1 ? 'step-3' : `step-3-${carCount}`;
        label = totalCars > 1 ? `Car #${carCount}` : 'Car';
      }

      this.steps.push({
        id: el.slot,
        type: el.type,
        label: label,
        slot: el.slot,
        panelId: panelId
      });
    }
    this.steps.push({ id: 'scene', type: 'scene', label: 'Put It Together', panelId: 'step-4' });
    this.steps.push({ id: 'checkout', type: 'checkout', label: 'Complete Order', panelId: 'step-5' });

    // Full reset: clear all slot state when rebuilding steps (design changed).
    // This ensures stale defaults from a previously clicked design are discarded.
    this.slotState = {};

    // Initialize per-slot state with the current design's defaults
    const defaults = design && design.defaultElements ? design.defaultElements : {};
    for (const el of sortedElements) {
      const def = defaults[el.slot];
      const ss = {
        mode: 'gallery',
        premadeCharId: null,
        premadeCarModelId: null,
        premadeCarVariantId: null,
        creator: null,
        recipe: null,
        galleryRendered: false,
        defaultElement: def || null
      };

      // Apply default element pre-selections
      if (def) {
        if (def.type === 'premade') {
          if (def.elementType === 'character') {
            ss.premadeCharId = def.premadeId;
          } else if (def.elementType === 'car') {
            ss.premadeCarModelId = def.premadeModelId || null;
            ss.premadeCarVariantId = def.premadeId;
          }
        } else if (def.type === 'builder') {
          // Builder defaults are applied when the builder is instantiated.
          // Set mode to builder so the builder UI is shown.
          ss.mode = 'builder';
        }
      }

      this.slotState[el.slot] = ss;
    }

    // Find the static panels using data attributes
    const modePanel = this.container.querySelector('[data-step-panel="mode-select"]');
    const designPanel = this.container.querySelector('[data-step-panel="design"]');
    const productPanel = this.container.querySelector('[data-step-panel="product"]');
    const scenePanel = this.container.querySelector('[data-step-panel="scene"]');
    const checkoutPanel = this.container.querySelector('[data-step-panel="checkout"]');

    // Update the ID of panels
    if (modePanel) modePanel.id = 'step-mode-select';
    if (designPanel) designPanel.id = 'step-1';
    if (productPanel) productPanel.id = 'step-product';

    // Remove old dynamically-injected element step panels
    const main = document.getElementById('app-main');
    if (main) {
      main.querySelectorAll('.step-panel[data-element-step]').forEach(el => el.remove());
    }

    // Inject new element step panels before the scene step panel
    if (main && scenePanel) {
      this.steps.forEach(step => {
        if (step.slot) {
          const el = sortedElements.find(e => e.slot === step.slot);
          if (el) {
            const div = document.createElement('div');
            div.innerHTML = this.renderElementStep(el, step.panelId);
            const panel = div.firstElementChild;
            main.insertBefore(panel, scenePanel);
            this.bindElementStep(el, step.panelId);
          }
        }
      });
    }

    // Update the IDs of scene and checkout panels (always step-4 and step-5)
    if (scenePanel) scenePanel.id = 'step-4';
    if (checkoutPanel) checkoutPanel.id = 'step-5';

    this.rebuildStepperNav();
  }

  /** Rebuild stepper nav HTML from this.steps */
  rebuildStepperNav() {
    const nav = document.getElementById('app-stepper');
    if (!nav) return;
    // Filter out mode-select step from the stepper nav (it's the entry screen)
    const navSteps = this.steps.filter(s => s.type !== 'mode-select');

    // Separate element steps (character/car) from non-element steps
    const elementSteps = navSteps.filter(s => s.type === 'character' || s.type === 'car');
    const useDropdown = elementSteps.length >= 4;

    if (useDropdown) {
      // Group element steps into a dropdown; render non-element steps as normal pills
      nav.innerHTML = this._buildDropdownStepper(navSteps, elementSteps);
      this._bindDropdownStepper(nav);
    } else {
      // Standard pill-button stepper (existing behavior)
      const maxReached = this._maxStepReached ?? this.currentStepIdx;
      nav.innerHTML = navSteps.map((s) => {
        const i = this.steps.indexOf(s);
        const active = i === this.currentStepIdx ? ' stepper__step--active' : '';
        const done = i !== this.currentStepIdx && i <= maxReached ? ' stepper__step--done' : '';
        const isLast = s === navSteps[navSteps.length - 1];
        const arrow = !isLast ? '<span class="stepper__arrow"></span>' : '';
        return `
          <button class="stepper__step${active}${done}" data-step-idx="${i}" data-step="${i + 1}">
            <span class="stepper__label">${s.label}</span>
          </button>${arrow}`;
      }).join('');
      nav.querySelectorAll('.stepper__step').forEach(btn => {
        btn.addEventListener('click', e => {
          const idx = parseInt(e.currentTarget.dataset.stepIdx);
          if (idx <= maxReached) this.goToStep(idx);
        });
      });
    }
  }

  /** Build stepper HTML with a dropdown for element steps */
  _buildDropdownStepper(navSteps, elementSteps) {
    const parts = [];
    let dropdownInserted = false;

    for (let ni = 0; ni < navSteps.length; ni++) {
      const s = navSteps[ni];
      const isElement = s.type === 'character' || s.type === 'car';
      const isLast = ni === navSteps.length - 1;

      if (isElement && !dropdownInserted) {
        // Insert the dropdown in place of the first element step
        dropdownInserted = true;

        // Determine dropdown state: active if current step is any element, done if past all elements
        const elementIndices = elementSteps.map(es => this.steps.indexOf(es));
        const maxElIdx = Math.max(...elementIndices);
        const minElIdx = Math.min(...elementIndices);
        const maxReached = this._maxStepReached ?? this.currentStepIdx;
        const currentIsElement = elementIndices.includes(this.currentStepIdx);
        const allDone = maxReached > maxElIdx;

        let dropdownClass = 'stepper__step stepper__dropdown-trigger';
        if (currentIsElement) dropdownClass += ' stepper__step--active';
        else if (allDone) dropdownClass += ' stepper__step--done';

        // Current label for the dropdown trigger
        const currentElStep = currentIsElement
          ? this.steps[this.currentStepIdx]
          : elementSteps[0];
        const triggerLabel = currentIsElement ? currentElStep.label : 'Characters';

        // Build dropdown items
        const items = elementSteps.map(es => {
          const idx = this.steps.indexOf(es);
          let itemClass = 'stepper__dropdown-item';
          if (idx === this.currentStepIdx) itemClass += ' stepper__dropdown-item--active';
          else if (idx <= maxReached) itemClass += ' stepper__dropdown-item--done';
          return `<button class="${itemClass}" data-step-idx="${idx}">${es.label}</button>`;
        }).join('');

        // Check if this is the last visible step in nav
        const remainingNonElement = navSteps.slice(ni + 1).filter(ns => ns.type !== 'character' && ns.type !== 'car');
        const isDropdownLast = remainingNonElement.length === 0 && isLast;
        const arrow = !isDropdownLast ? '<span class="stepper__arrow"></span>' : '';

        parts.push(`
          <div class="stepper__dropdown">
            <button class="${dropdownClass}">
              <span class="stepper__label">${triggerLabel}</span>
              <span class="stepper__dropdown-caret">▾</span>
            </button>
            <div class="stepper__dropdown-menu">${items}</div>
          </div>${arrow}`);

      } else if (isElement) {
        // Skip subsequent element steps — they're in the dropdown
        continue;
      } else {
        // Non-element step: render as normal pill
        const i = this.steps.indexOf(s);
        const active = i === this.currentStepIdx ? ' stepper__step--active' : '';
        const done = i !== this.currentStepIdx && i <= (this._maxStepReached ?? this.currentStepIdx) ? ' stepper__step--done' : '';
        const isLastNav = ni === navSteps.length - 1 ||
          navSteps.slice(ni + 1).every(ns => ns.type === 'character' || ns.type === 'car');
        const arrow = !isLastNav ? '<span class="stepper__arrow"></span>' : '';
        parts.push(`
          <button class="stepper__step${active}${done}" data-step-idx="${i}" data-step="${i + 1}">
            <span class="stepper__label">${s.label}</span>
          </button>${arrow}`);
      }
    }
    return parts.join('');
  }

  /** Bind click handlers for the dropdown stepper */
  _bindDropdownStepper(nav) {
    const maxReached = this._maxStepReached ?? this.currentStepIdx;
    // Regular step buttons
    nav.querySelectorAll('.stepper__step:not(.stepper__dropdown-trigger)').forEach(btn => {
      btn.addEventListener('click', e => {
        const idx = parseInt(e.currentTarget.dataset.stepIdx);
        if (idx <= maxReached) this.goToStep(idx);
      });
    });

    // Dropdown trigger: toggle menu
    const trigger = nav.querySelector('.stepper__dropdown-trigger');
    const menu = nav.querySelector('.stepper__dropdown-menu');
    if (trigger && menu) {
      trigger.addEventListener('click', (e) => {
        e.stopPropagation();
        const isOpen = menu.classList.toggle('stepper__dropdown-menu--open');
        trigger.classList.toggle('stepper__dropdown-trigger--open', isOpen);
      });

      // Dropdown items: navigate to step
      menu.querySelectorAll('.stepper__dropdown-item').forEach(item => {
        item.addEventListener('click', (e) => {
          e.stopPropagation();
          const idx = parseInt(e.currentTarget.dataset.stepIdx);
          menu.classList.remove('stepper__dropdown-menu--open');
          trigger.classList.remove('stepper__dropdown-trigger--open');
          if (idx <= maxReached) this.goToStep(idx);
        });
      });

      // Close dropdown when clicking outside
      document.addEventListener('click', () => {
        menu.classList.remove('stepper__dropdown-menu--open');
        trigger.classList.remove('stepper__dropdown-trigger--open');
      }, { once: false });
    }
  }

  renderModeSelection() {
    return `
      <section id="step-mode-select" class="step-panel step-panel--active" data-step-panel="mode-select">
        <div class="design-hero">
          <div class="design-hero__spacer" style="width:52px;flex-shrink:0;">&#8203;</div>
          <div class="design-hero__brand">
            <h1 class="design-hero__title">DreamMee</h1>
            <p class="design-hero__subtitle">made on site customizable apparel</p>
          </div>
          <div class="design-hero__spacer" style="width:52px;flex-shrink:0;">&#8203;</div>
        </div>
        <div class="mode-selection">
          <div class="mode-selection__cards">
            <div class="mode-selection__card mode-selection__card--convention" data-order-mode="convention">
              <h2 class="mode-selection__card-title">Convention Order</h2>
              <p class="mode-selection__card-subtitle">IN-PERSON ONLY</p>
            </div>
            <div class="mode-selection__card mode-selection__card--shipping" data-order-mode="shipping">
              <h2 class="mode-selection__card-title">Online Order</h2>
              <p class="mode-selection__card-subtitle">SHIPPING ONLY</p>
            </div>
          </div>
        </div>
      </section>`;
  }

  renderStep1() {
    return `
      <section id="step-1" class="step-panel step-panel--hidden" data-step-panel="design">
        <div class="design-hero">
          <div class="design-hero__spacer" style="width:52px;flex-shrink:0;">&#8203;</div>
          <div class="design-hero__brand">
            <h1 class="design-hero__title">DreamMee</h1>
            <p class="design-hero__subtitle">made on site customizable apparel</p>
          </div>
          <button class="design-hero__cart" id="btn-cart" title="View Cart">
            <img src="${resolveAssetUrl('demo-assets/shopping_cart.webp')}" alt="Cart" width="47" height="47">
            <span class="cart-badge cart-badge--hidden" id="cart-badge">0</span>
          </button>
        </div>
        <div class="design-page">
          <p class="design-page__heading">Choose a background to get started</p>
          <div class="design-grid" id="design-grid"></div>
          <button class="btn btn--primary btn--lg design-page__next" id="step1-next">Next →</button>
        </div>
      </section>`;
  }

  /** Generate a step panel HTML string for a character or car element slot */
  renderElementStep(el, panelId) {
    const slot = el.slot;
    const prefix = this.getSlotPrefix(slot);
    const isChar = el.type === 'character';
    const previewClass = isChar ? '' : ' premade-preview--landscape';
    const canvasClass = isChar ? '' : ' canvas-wrapper--landscape';
    const emptyText = isChar ? 'Select a character from the gallery' : 'Select a car from the gallery';
    const gridTitle = isChar ? 'Choose a Character' : 'Choose a Car';
    const modeAttr = `data-${prefix}-mode`;
    const tabClass = `${prefix}-mode-tab mode-tab`;
    const canvasContainerId = slot === 'character_1' ? 'character-canvas-container' : `${prefix}-canvas-container`;
    const controlsPanelId = slot === 'character_1' ? 'character-controls' : `${prefix}-controls`;

    let nextBtnId = `${panelId}-next`;
    let backBtnId = `${panelId}-back`;
    if (panelId === 'step-2') {
      nextBtnId = 'step2-next';
      backBtnId = 'step2-back';
    } else if (panelId === 'step-3') {
      nextBtnId = 'step3-next';
      backBtnId = 'step3-back';
    }

    return `
      <section id="${panelId}" class="step-panel step-panel--hidden" data-element-step="${slot}">
        <!-- Gallery Mode -->
        <div class="mode-content mode-content--active" id="${prefix}-gallery-content">
          <div class="premade-layout">
            <div class="premade-preview-wrap">
              <div class="premade-preview${previewClass}" id="${prefix}-premade-preview">
                <div class="premade-preview__empty">${emptyText}</div>
              </div>
              ${!isChar ? `<div class="premade-preview__variants" id="${prefix}-variant-section"></div>` : ''}
            </div>
            <div class="premade-grid-container">
              <div class="premade-grid-wrap">
                <div class="mode-toggle">
                  <button class="mode-toggle__btn mode-toggle__btn--active ${prefix}-mode-tab mode-tab mode-tab--active" ${modeAttr}="gallery">Choose Pre-Made</button>
                  <button class="mode-toggle__btn ${prefix}-mode-tab mode-tab" ${modeAttr}="builder">Create Your Own</button>
                </div>
                <div class="premade-grid-divider"></div>
                ${!isChar ? `
                <div class="premade-select-wrap">
                  <select class="premade-brand-select" id="${prefix}-brand-select">
                    <option value="all">Show All</option>
                  </select>
                  <span class="premade-select-arrow"></span>
                </div>
                ` : ''}
                <div class="premade-grid-scroll" id="${prefix}-premade-scroll">
                  <div class="premade-grid" id="${prefix}-premade-grid"></div>
                  <div class="premade-grid-fade"></div>
                </div>
                <button class="btn btn--primary btn--continue" id="${nextBtnId}">Continue</button>
              </div>
            </div>
          </div>
        </div>
        <!-- Builder Mode -->
        <div class="mode-content" id="${prefix}-builder-content">
          <div class="step-content step-content--two-col">
            <div class="canvas-area">
              <div class="canvas-wrapper${canvasClass}" id="${prefix}-canvas-wrapper">
                <div id="${canvasContainerId}"></div>
              </div>
              ${isChar ? `<div class="canvas-toolbar">
                <button class="toolbar-btn" id="${prefix}-randomize" title="Randomize">🎲</button>
                <button class="toolbar-btn" id="${prefix}-reset" title="Reset">↺</button>
              </div>` : ''}
            </div>
            <div class="controls-panel-wrapper">
              <div class="mode-toggle">
                <button class="mode-toggle__btn ${prefix}-mode-tab mode-tab" ${modeAttr}="gallery">Choose Pre-Made</button>
                <button class="mode-toggle__btn mode-toggle__btn--active ${prefix}-mode-tab mode-tab mode-tab--active" ${modeAttr}="builder">Create Your Own</button>
              </div>
              <div class="premade-grid-divider"></div>
              <div class="controls-panel" id="${controlsPanelId}"></div>
              <button class="btn btn--primary btn--continue" id="${panelId}-builder-next">Continue</button>
            </div>
          </div>
        </div>
      </section>`;
  }

  /** Bind events for a dynamically-created element step panel */
  bindElementStep(el, panelId) {
    const slot = el.slot;
    const prefix = this.getSlotPrefix(slot);
    const isChar = el.type === 'character';
    const ss = this.slotState[slot];
    const modeAttr = `data-${prefix}-mode`;
    const panel = document.getElementById(panelId);
    if (!panel) return;

    const canvasContainerId = slot === 'character_1' ? 'character-canvas-container' : `${prefix}-canvas-container`;
    const controlsPanelId = slot === 'character_1' ? 'character-controls' : `${prefix}-controls`;

    // Mode tabs (gallery ↔ builder)
    panel.querySelectorAll(`[${modeAttr}]`).forEach(tab => {
      tab.addEventListener('click', e => {
        const mode = e.currentTarget.getAttribute(modeAttr);
        ss.mode = mode;
        panel.querySelectorAll(`[${modeAttr}]`).forEach(t => {
          t.classList.toggle('mode-tab--active', t.getAttribute(modeAttr) === mode);
          t.classList.toggle('mode-toggle__btn--active', t.getAttribute(modeAttr) === mode);
        });
        document.getElementById(`${prefix}-gallery-content`).classList.toggle('mode-content--active', mode === 'gallery');
        document.getElementById(`${prefix}-builder-content`).classList.toggle('mode-content--active', mode === 'builder');
        if (mode === 'builder' && !ss.creator) {
          if (isChar) {
            const design = this.getSelectedDesign();
            const allowedPoses = design ? (design.poses || design.allowedPoses) : null;
            ss.creator = new CharacterCreator(canvasContainerId, controlsPanelId, CHARACTER_CONFIG, { allowedPoses, app: this });
          } else {
            ss.creator = new CarCreator(CAR_CONFIG, { canvasContainerId: canvasContainerId, controlsPanelId: controlsPanelId });
            ss.creator.preloadAllAssets().then(() => ss.creator.loadAll());
          }
          setTimeout(() => ss.creator.handleResize(), 50);
        }
        if (mode === 'builder' && ss.creator) {
          setTimeout(() => ss.creator.handleResize(), 50);
        }
      });
    });

    // Gallery Continue button + Builder Next button
    let nextBtnId = `${panelId}-next`;
    let backBtnId = `${panelId}-back`;
    if (panelId === 'step-2') {
      nextBtnId = 'step2-next';
      backBtnId = 'step2-back';
    } else if (panelId === 'step-3') {
      nextBtnId = 'step3-next';
      backBtnId = 'step3-back';
    }

    // Helper: handle "Continue" from an element step — may show add-element popup
    const handleElementContinue = () => {
      if (ss.mode === 'builder' && ss.creator) {
        ss.recipe = ss.creator.generateRecipe();
      }
      // If there are pending characters or cars, show the add-element popup
      const hasPendingChars = this.pendingCharacterElements && this.pendingCharacterElements.length > 0;
      const hasPendingCars = this.pendingCarElements && this.pendingCarElements.length > 0;
      if (hasPendingChars || hasPendingCars) {
        this.showAddElementPopup();
      } else {
        this.goToStep(this.currentStepIdx + 1);
      }
    };

    // Gallery mode Continue button
    const nextBtn = document.getElementById(nextBtnId);
    if (nextBtn) nextBtn.addEventListener('click', handleElementContinue);

    // Builder mode Back / Next buttons
    const backBtn = document.getElementById(backBtnId);
    const builderNextBtn = document.getElementById(`${panelId}-builder-next`);
    if (backBtn) backBtn.addEventListener('click', () => this.goToStep(this.currentStepIdx - 1));
    if (builderNextBtn) builderNextBtn.addEventListener('click', () => {
      if (ss.creator) {
        ss.recipe = ss.creator.generateRecipe();
      }
      const hasPendingChars = this.pendingCharacterElements && this.pendingCharacterElements.length > 0;
      const hasPendingCars = this.pendingCarElements && this.pendingCarElements.length > 0;
      if (hasPendingChars || hasPendingCars) {
        this.showAddElementPopup();
      } else {
        this.goToStep(this.currentStepIdx + 1);
      }
    });

    // Add shirt size and color select overlays to the previews
    const canvasWrapper = panel.querySelector(`#${prefix}-canvas-wrapper`);
    if (canvasWrapper) {
      new PreviewShirtSelector(this, canvasWrapper, `${prefix}-builder`);
    }
    const premadePreviewWrap = panel.querySelector('.premade-preview-wrap');
    if (premadePreviewWrap) {
      new PreviewShirtSelector(this, premadePreviewWrap, `${prefix}-gallery`);
    }

    // ---- Apply builder defaults if slot is pre-configured for builder mode ----
    if (ss.mode === 'builder' && ss.defaultElement && ss.defaultElement.type === 'builder') {
      const def = ss.defaultElement;

      // Switch UI to builder mode
      panel.querySelectorAll(`[${modeAttr}]`).forEach(t => {
        t.classList.toggle('mode-tab--active', t.getAttribute(modeAttr) === 'builder');
        t.classList.toggle('mode-toggle__btn--active', t.getAttribute(modeAttr) === 'builder');
      });
      document.getElementById(`${prefix}-gallery-content`).classList.remove('mode-content--active');
      document.getElementById(`${prefix}-builder-content`).classList.add('mode-content--active');

      // Create the builder and apply default selections
      if (isChar) {
        const design = this.getSelectedDesign();
        const allowedPoses = design ? (design.poses || design.allowedPoses) : null;
        if (ss.creator && typeof ss.creator.destroy === 'function') {
          ss.creator.destroy();
        }
        ss.creator = new CharacterCreator(canvasContainerId, controlsPanelId, CHARACTER_CONFIG, { allowedPoses, app: this });
        // Apply pose first (triggers layer recreation and asset remapping)
        if (def.pose && def.pose !== ss.creator.state.pose) {
          ss.creator.setPose(def.pose);
        }
        // Apply individual part selections
        if (def.selections) {
          for (const [cat, assetId] of Object.entries(def.selections)) {
            if (cat === 'pose') continue; // already handled
            if (assetId !== undefined) {
              ss.creator.state.selections[cat] = assetId;
            }
          }
        }
        // Apply multi-selections (e.g. extras)
        if (def.multiSelections) {
          for (const [cat, arr] of Object.entries(def.multiSelections)) {
            ss.creator.state.multiSelections[cat] = [...arr];
          }
        }
        // Apply tone selections
        if (def.toneSelections) {
          for (const [group, toneId] of Object.entries(def.toneSelections)) {
            ss.creator.state.toneSelections[group] = toneId;
          }
        }
        // Re-render controls and reload character with the new selections
        ss.creator.renderControls();
        ss.creator.loadCharacter();
      } else {
        if (ss.creator && typeof ss.creator.destroy === 'function') {
          ss.creator.destroy();
        }
        ss.creator = new CarCreator(CAR_CONFIG, { canvasContainerId, controlsPanelId });
        // Apply model first
        if (def.model && def.model !== ss.creator.state.model) {
          ss.creator.state.model = def.model;
          ss.creator.recreateLayers();
        }
        // Apply part selections
        if (def.selections) {
          for (const [cat, assetId] of Object.entries(def.selections)) {
            if (cat === 'model') continue;
            if (assetId !== undefined) {
              ss.creator.state.selections[cat] = assetId;
            }
          }
        }
        // Apply tone selections
        if (def.toneSelections) {
          for (const [group, toneId] of Object.entries(def.toneSelections)) {
            ss.creator.state.toneSelections[group] = toneId;
          }
        }
        ss.creator.renderControls();
        ss.creator.preloadAllAssets().then(() => ss.creator.loadAll());
      }
      setTimeout(() => ss.creator.handleResize(), 50);
    }
  }

  renderProductStep() {
    const sizes = DreamMeeApp.SHIRT_SIZES;
    const colors = DreamMeeApp.SHIRT_COLORS;
    return `
      <section id="step-product" class="step-panel step-panel--hidden" data-step-panel="product">
        <div class="design-page">
          <div class="product-detail">
            <div class="product-detail__image">
              <img id="product-detail-img" src="" alt="Design Preview" />
            </div>
            <div class="product-detail__info">
              <h2 class="product-detail__title" id="product-detail-title">Select Your Design</h2>

              <div class="product-detail__section">
                <h3 class="product-detail__label">Choose Product Type:</h3>
                <div class="product-detail__types" id="product-type-grid">
                  <button class="pd-type-btn pd-type-btn--active" data-type="tshirt">T-Shirt</button>
                  <button class="pd-type-btn" data-type="hoodie">Hoodie</button>
                </div>
              </div>

              <div class="product-detail__section">
                <h3 class="product-detail__label">Choose A Size: <span class="product-detail__label-hint">(US Universal Fit)</span></h3>
                <div class="product-detail__sizes" id="product-size-grid">
                  ${sizes.map(s => `<button class="pd-size-btn${s === 'S' ? ' pd-size-btn--active' : ''}" data-size="${s}">${s}</button>`).join('')}
                </div>
              </div>

              <div class="product-detail__section">
                <h3 class="product-detail__label">Choose A Blank Color:</h3>
                <div class="product-detail__colors" id="product-color-grid">
                  ${colors.map((c, i) => `<button class="pd-color-swatch${i === 0 ? ' pd-color-swatch--active' : ''}" data-color="${c.hex}" data-color-name="${c.name}" style="background:${c.hex}" title="${c.name}"></button>`).join('')}
                </div>
              </div>

              <div class="product-detail__desc" id="product-detail-desc">
                <p>100% Cotton Oversized US Fit</p>
                <p>Sizing Chart (Or Ask A Staff Member)</p>
                <div class="product-detail__includes" style="margin-top: 16px;">
                  <p>Includes:</p>
                  <p>Up To 2 Characters</p>
                  <p>Up To 1 Car</p>
                  <p>Misc. Accessories</p>
                </div>
              </div>

              <button class="btn btn--primary btn--lg product-detail__cta" id="product-step-next">Start Customizing</button>
            </div>
          </div>
        </div>
      </section>`;
  }

  renderStep4() {
    return `
      <section id="step-scene" class="step-panel step-panel--hidden" data-step-panel="scene">
        <div class="step-content step-content--two-col">
          <div class="canvas-area">
            <div class="canvas-wrapper canvas-wrapper--scene" id="scene-canvas-wrapper">
              <div id="scene-canvas-container"></div>
              <div class="scene-hint" id="scene-hint">
                <div class="scene-hint__icon">👆</div>
                <div class="scene-hint__text">Click to select · Drag to move · Corners to resize</div>
              </div>
            </div>
          </div>
          <div class="controls-panel-wrapper">
            <div class="controls-panel" id="scene-controls"></div>
            <button class="btn btn--primary btn--continue" id="step4-next">Continue</button>
          </div>
        </div>
      </section>`;
  }

  renderStep5() {
    const isShopify = typeof DREAMMEE_DEPLOY !== 'undefined' && DREAMMEE_DEPLOY.mode === 'shopify';
    const sizes = DreamMeeApp.SHIRT_SIZES;
    const colors = DreamMeeApp.SHIRT_COLORS;

    return `
      <section id="step-checkout" class="step-panel step-panel--hidden" data-step-panel="checkout">
        <div class="step-content step-content--two-col checkout-two-col">
          <div class="canvas-area">
            <div class="checkout-preview-wrap">
              <div class="checkout-preview__img" id="checkout-preview"></div>
              <div class="checkout-summary" id="checkout-summary"></div>
            </div>
          </div>
          <div class="controls-panel-wrapper">
            <h2 class="checkout-panel__title">Order Summary</h2>

            <div class="checkout-info-section">
              <label class="checkout-field__label" for="checkout-fullname">Full Name<span class="checkout-field__required">*</span></label>
              <input type="text" id="checkout-fullname" class="checkout-field__input" placeholder="" autocomplete="name" required />
            </div>

            <div class="checkout-info-section">
              <div class="checkout-field__label-row">
                <label class="checkout-field__label" for="checkout-phone">Phone Number<span class="checkout-field__required">*</span></label>
                <span class="checkout-field__hint">For order status updates</span>
              </div>
              <input type="tel" id="checkout-phone" class="checkout-field__input" placeholder="" autocomplete="tel" required />
              
              <div class="checkout-sms-consent-wrapper" style="margin-top: 8px; display: flex; gap: 8px; align-items: flex-start;">
                <input type="checkbox" id="checkout-sms-consent" style="margin-top: 3px; cursor: pointer;" />
                <label for="checkout-sms-consent" style="font-size: 0.8rem; color: #e4e4e7; line-height: 1.4; cursor: pointer; user-select: none;">
                  I agree to receive a one-time automated SMS notification when my order is ready for pickup. Consent is not a condition of purchase. Message and data rates may apply. Reply HELP for help, STOP to cancel.
                </label>
              </div>
              
              <div style="margin-top: 6px; font-size: 0.75rem; color: #a1a1aa;">
                Read our <a href="https://points.dreamscapecc.com/policies/terms-of-service" target="_blank" style="color: #c084fc; text-decoration: underline;">Terms of Service</a> and <a href="https://points.dreamscapecc.com/policies/privacy-policy" target="_blank" style="color: #c084fc; text-decoration: underline;">Privacy Policy</a>.
              </div>
            </div>

            <div class="checkout-info-section">
              <div class="checkout-field__label-row">
                <label class="checkout-field__label" for="checkout-address">Address<span class="checkout-field__required">*</span></label>
                <span class="checkout-field__hint">Your address is required in case we have to ship it to you post convention</span>
              </div>
              <input type="text" id="checkout-address" class="checkout-field__input" placeholder="" autocomplete="street-address" required />
            </div>

            <div class="checkout-info-section">
              <h3 class="checkout-info__label" id="checkout-info-label">Shirt Information</h3>
              <div class="checkout-shirt-selectors">
                <div class="checkout-shirt-select">
                  <select class="checkout-select" id="checkout-type-select">
                    <option value="tshirt">Product: T-Shirt</option>
                    <option value="hoodie">Product: Hoodie</option>
                  </select>
                  <span class="checkout-select-arrow"></span>
                </div>
                <div class="checkout-shirt-select">
                  <select class="checkout-select" id="checkout-size-select">
                    ${sizes.map(s => `<option value="${s}"${s === 'S' ? ' selected' : ''}>Size: ${s}</option>`).join('')}
                  </select>
                  <span class="checkout-select-arrow"></span>
                </div>
                <div class="checkout-shirt-select">
                  <select class="checkout-select" id="checkout-color-select">
                    ${colors.map((c, i) => `<option value="${c.hex}" data-color-name="${c.name}"${i === 0 ? ' selected' : ''}>Color: ${c.name}</option>`).join('')}
                  </select>
                  <span class="checkout-select-arrow"></span>
                </div>
              </div>
            </div>

            <div class="checkout-info-section">
              <div class="modal__notes" id="modal-notes-section">
                <button class="modal__notes-toggle" id="modal-notes-toggle" type="button">📝 Add a note <span class="modal__notes-optional">(optional)</span><span class="modal__notes-chevron">&#x25B8;</span></button>
                <div class="modal__notes-body" id="modal-notes-body">
                  <textarea
                     class="modal__notes-input"
                     id="modal-notes-input"
                     placeholder="E.g. &quot;I want the design on the front&quot;, &quot;Please make the background lighter&quot;..."
                     maxlength="1000"
                     rows="3"
                  ></textarea>
                  <div class="modal__notes-counter"><span id="modal-notes-count">0</span> / 1000</div>
                </div>
              </div>
            </div>

            <button class="btn btn--primary btn--continue btn--add-to-cart" id="modal-add-to-cart">Add to Cart</button>

            <div class="checkout-debug-section${isShopify ? ' modal__btn--hidden' : ''}">
              <div class="modal__actions-divider"><span>Debug</span></div>
              <div class="checkout-debug-actions">
                <button class="btn btn--ghost btn--sm" id="modal-download">📥 Download PNG</button>
                <button class="btn btn--ghost btn--sm" id="modal-recipe">📋 Recipe JSON</button>
              </div>
            </div>

            <div class="modal__status" id="modal-status" style="display:none;">
              <p id="modal-status-text"></p>
            </div>
            <div class="modal__recipe" id="modal-recipe-display" style="display:none;">
              <pre id="modal-recipe-json"></pre>
            </div>
          </div>
        </div>
      </section>`;
  }

  // ==================== INIT & BIND ====================

  init() {
    this.rebuildStepperNav();
    this.bindModeSelection();
    this.bindStep1();
    this.bindProductStep();
    this.bindStep5();
    this.bindSceneNav();
    this.bindCheckoutActions();
    this.bindCartViewer();
    this.bindPostAddPopup();
    this.bindAddCharPopup();
    this._updateCartBadge();

    // Home button navigates to step 1 (Design step, after mode selection)
    const homeBtn = document.getElementById('btn-home');
    if (homeBtn) {
      homeBtn.addEventListener('click', () => {
        this.goToStep(1);
      });
    }

    // Initially hide header since we start on the mode selection step
    const header = document.getElementById('app-header');
    if (header) {
      header.classList.add('app-header--hidden');
    }
  }

  /** Bind events for the Product (Size & Color) step */
  bindProductStep() {
    // Product Type buttons
    this.container.querySelectorAll('#product-type-grid .pd-type-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const newType = e.currentTarget.dataset.type;
        const oldType = this.state.product.type;
        if (newType !== oldType) {
          this.state.product.type = newType;
          this.syncShirtOptions();
          this._populateProductStep();
        }
      });
    });

    // Size buttons
    this.container.querySelectorAll('#product-size-grid .pd-size-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        this.state.product.size = e.currentTarget.dataset.size;
        this.syncShirtOptions();
      });
    });

    // Color swatches
    this.container.querySelectorAll('#product-color-grid .pd-color-swatch').forEach(swatch => {
      swatch.addEventListener('click', (e) => {
        this.state.product.color = e.currentTarget.dataset.color;
        this.state.product.colorName = e.currentTarget.dataset.colorName;
        this.syncShirtOptions();
      });
    });

    // CTA button
    document.getElementById('product-step-next').addEventListener('click', () => {
      this.goToStep(this.currentStepIdx + 1);
    });
  }

  /** Synchronize shirt size and color choices across all views and inputs */
  syncShirtOptions() {
    const { type, size, color } = this.state.product;
    const isHoodie = type === 'hoodie';
    const label = isHoodie ? 'Hoodie' : 'T-Shirt';

    // 1. Sync product step buttons/swatches
    this.container.querySelectorAll('#product-size-grid .pd-size-btn').forEach(b => {
      b.classList.toggle('pd-size-btn--active', b.dataset.size === size);
    });
    this.container.querySelectorAll('#product-color-grid .pd-color-swatch').forEach(s => {
      s.classList.toggle('pd-color-swatch--active', s.dataset.color === color);
    });
    this.container.querySelectorAll('#product-type-grid .pd-type-btn').forEach(b => {
      b.classList.toggle('pd-type-btn--active', b.dataset.type === type);
    });

    // 2. Sync checkout selects
    const infoLabel = document.getElementById('checkout-info-label');
    if (infoLabel) {
      infoLabel.textContent = `${label} Information`;
    }

    const checkoutType = document.getElementById('checkout-type-select');
    if (checkoutType) {
      checkoutType.value = type;
    }

    const checkoutSize = document.getElementById('checkout-size-select');
    if (checkoutSize) {
      checkoutSize.value = size;
      Array.from(checkoutSize.options).forEach(opt => {
        opt.text = `Size: ${opt.value}`;
      });
    }

    const checkoutColor = document.getElementById('checkout-color-select');
    if (checkoutColor) {
      checkoutColor.value = color;
      Array.from(checkoutColor.options).forEach(opt => {
        const colorName = opt.dataset.colorName || opt.value;
        opt.text = `Color: ${colorName}`;
      });
    }

    // 3. Sync all preview overlay selects
    this.container.querySelectorAll('.preview-select').forEach(select => {
      if (select.id.includes('type')) {
        select.value = type;
      } else if (select.id.includes('size')) {
        select.value = size;
        Array.from(select.options).forEach(opt => {
          opt.text = `Size: ${opt.value}`;
        });
      } else if (select.id.includes('color')) {
        select.value = color;
        Array.from(select.options).forEach(opt => {
          const colorName = opt.dataset.colorName || opt.getAttribute('data-color-name') || opt.value;
          opt.text = `Color: ${colorName}`;
        });
      }
    });

    // 4. Update checkout summary
    if (typeof this._renderCheckoutSummary === 'function') {
      this._renderCheckoutSummary();
    }
  }

  /** Sync checkout step's size select with the product step selection */
  _syncCheckoutSize() {
    this.syncShirtOptions();
  }

  /** Sync checkout step's color select with the product step selection */
  _syncCheckoutColor() {
    this.syncShirtOptions();
  }

  // ==================== MODE SELECTION ====================

  bindModeSelection() {
    this.container.querySelectorAll('[data-order-mode]').forEach(card => {
      card.addEventListener('click', (e) => {
        const mode = e.currentTarget.dataset.orderMode;
        this.state.orderMode = mode;
        // Navigate to design step (index 1)
        this.goToStep(1);
      });
    });
  }

  // ==================== CONVENTION CART ====================

  _loadCart() {
    try {
      const data = sessionStorage.getItem('dreammee_cart');
      return data ? JSON.parse(data) : [];
    } catch { return []; }
  }

  _saveCart() {
    try {
      sessionStorage.setItem('dreammee_cart', JSON.stringify(this.conventionCart));
    } catch (e) {
      console.warn('[DreamMee] Failed to save cart:', e);
    }
  }

  addToConventionCart(item) {
    this.conventionCart.push(item);
    this._saveCart();
    this._updateCartBadge();
  }

  removeFromConventionCart(itemId) {
    this.conventionCart = this.conventionCart.filter(i => i.id !== itemId);
    this._saveCart();
    this._updateCartBadge();
  }

  clearConventionCart() {
    this.conventionCart = [];
    this._saveCart();
    this._updateCartBadge();
  }

  _updateCartBadge() {
    const badge = document.getElementById('cart-badge');
    if (!badge) return;
    const count = this.conventionCart.length;
    badge.textContent = count;
    badge.classList.toggle('cart-badge--hidden', count === 0);
  }

  // ==================== POST-ADD POPUP ====================

  renderPostAddPopup() {
    return `
      <div class="post-add-overlay" id="post-add-overlay" style="display:none;">
        <div class="post-add-modal">
          <button class="post-add-modal__close" id="post-add-close">&times;</button>
          <h2 class="post-add-modal__title">All Done?</h2>
          <p class="post-add-modal__desc" id="post-add-desc"></p>
          <p class="post-add-modal__or">OR</p>
          <p class="post-add-modal__desc" id="post-add-desc2"></p>
          <div class="post-add-modal__actions">
            <button class="post-add-modal__btn post-add-modal__btn--primary" id="post-add-primary"></button>
            <button class="post-add-modal__btn post-add-modal__btn--secondary" id="post-add-secondary">Add Another Design</button>
          </div>
        </div>
      </div>`;
  }

  /** Render the "Add Another Element?" popup (supports both characters and cars) */
  renderAddCharPopup() {
    return `
      <div class="post-add-overlay" id="add-char-overlay" style="display:none;">
        <div class="post-add-modal add-char-modal">
          <button class="post-add-modal__close" id="add-char-close">&times;</button>
          <div class="add-char-modal__icon">✨</div>
          <h2 class="post-add-modal__title">Add More?</h2>
          <p class="post-add-modal__desc">Your design supports more elements. Would you like to add another one?</p>
          <div class="post-add-modal__actions">
            <button class="post-add-modal__btn post-add-modal__btn--primary" id="add-char-yes" style="display:none;">Add Another Character</button>
            <button class="post-add-modal__btn post-add-modal__btn--primary" id="add-car-yes" style="display:none;">Add Another Car</button>
            <button class="post-add-modal__btn post-add-modal__btn--secondary" id="add-char-no">No, Continue</button>
          </div>
        </div>
      </div>`;
  }

  bindAddCharPopup() {
    const overlay = document.getElementById('add-char-overlay');
    if (!overlay) return;

    const dismissAndAdvance = () => {
      overlay.style.display = 'none';
      this._skipRemainingElements();
      this.goToStep(this.currentStepIdx + 1);
    };

    document.getElementById('add-char-close').addEventListener('click', dismissAndAdvance);

    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) dismissAndAdvance();
    });

    // "Add Character" — inject the next character step
    document.getElementById('add-char-yes').addEventListener('click', () => {
      overlay.style.display = 'none';
      this.addNextElementStep('character');
    });

    // "Add Car" — inject the next car step
    document.getElementById('add-car-yes').addEventListener('click', () => {
      overlay.style.display = 'none';
      this.addNextElementStep('car');
    });

    // "No" — skip remaining and advance
    document.getElementById('add-char-no').addEventListener('click', dismissAndAdvance);
  }

  /** Show the add-element popup, updating buttons based on what's pending */
  showAddElementPopup() {
    const overlay = document.getElementById('add-char-overlay');
    if (!overlay) return;

    const remainingChars = this.pendingCharacterElements ? this.pendingCharacterElements.length : 0;
    const remainingCars = this.pendingCarElements ? this.pendingCarElements.length : 0;

    // Update title and description based on what's available
    const title = overlay.querySelector('.post-add-modal__title');
    const desc = overlay.querySelector('.post-add-modal__desc');

    if (remainingChars > 0 && remainingCars > 0) {
      if (title) title.textContent = 'Add More?';
      if (desc) desc.textContent = `You can add ${remainingChars} more character${remainingChars > 1 ? 's' : ''} and ${remainingCars} more car${remainingCars > 1 ? 's' : ''}.`;
    } else if (remainingChars > 0) {
      if (title) title.textContent = 'Add Another Character?';
      if (desc) desc.textContent = remainingChars === 1
        ? 'Your design supports one more character. Would you like to customize it?'
        : `Your design supports ${remainingChars} more characters. Would you like to add another one?`;
    } else if (remainingCars > 0) {
      if (title) title.textContent = 'Add Another Car?';
      if (desc) desc.textContent = remainingCars === 1
        ? 'Your design supports one more car. Would you like to add it?'
        : `Your design supports ${remainingCars} more cars. Would you like to add another one?`;
    }

    // Show/hide the appropriate buttons
    const charBtn = document.getElementById('add-char-yes');
    const carBtn = document.getElementById('add-car-yes');
    if (charBtn) charBtn.style.display = remainingChars > 0 ? '' : 'none';
    if (carBtn) carBtn.style.display = remainingCars > 0 ? '' : 'none';

    overlay.style.display = 'flex';
  }

  // Legacy alias for showAddElementPopup
  showAddCharPopup() { this.showAddElementPopup(); }

  /** Inject the next pending element (character or car) as a new step and navigate to it */
  addNextElementStep(elementType) {
    const isChar = elementType === 'character';
    const pendingArr = isChar ? this.pendingCharacterElements : this.pendingCarElements;
    if (!pendingArr || pendingArr.length === 0) return;

    const nextEl = pendingArr.shift();
    const design = this.getSelectedDesign();
    const defaults = design && design.defaultElements ? design.defaultElements : {};

    // Determine numbering based on how many of this type are already in steps
    const existingSteps = this.steps.filter(s => s.type === elementType).length;
    const num = existingSteps + 1;
    const allOfType = isChar ? this._allCharacterElements : this._allCarElements;
    const hasMultiple = allOfType && allOfType.length > 1;
    const typeLabel = isChar ? 'Character' : 'Car';
    const panelId = isChar ? `step-2-${num}` : `step-3-${num}`;
    const label = hasMultiple ? `${typeLabel} #${num}` : typeLabel;

    // Create the step entry
    const newStep = {
      id: nextEl.slot,
      type: elementType,
      label: label,
      slot: nextEl.slot,
      panelId: panelId
    };

    // Insert the step right after the last step of the same type
    let insertIdx = -1;
    for (let i = this.steps.length - 1; i >= 0; i--) {
      if (this.steps[i].type === elementType) {
        insertIdx = i + 1;
        break;
      }
    }
    // If no existing step of this type, insert after the last element step
    if (insertIdx === -1) {
      for (let i = this.steps.length - 1; i >= 0; i--) {
        if (this.steps[i].type === 'character' || this.steps[i].type === 'car') {
          insertIdx = i + 1;
          break;
        }
      }
    }
    if (insertIdx === -1) return;
    this.steps.splice(insertIdx, 0, newStep);

    // Initialize slot state for the new element
    const def = defaults[nextEl.slot];
    const ss = {
      mode: 'gallery',
      premadeCharId: null,
      premadeCarModelId: null,
      premadeCarVariantId: null,
      creator: null,
      recipe: null,
      galleryRendered: false,
      defaultElement: def || null
    };
    if (def) {
      if (def.type === 'premade') {
        if (def.elementType === 'character') {
          ss.premadeCharId = def.premadeId;
        } else if (def.elementType === 'car') {
          ss.premadeCarModelId = def.premadeModelId || null;
          ss.premadeCarVariantId = def.premadeId;
        }
      } else if (def.type === 'builder') {
        ss.mode = 'builder';
      }
    }
    this.slotState[nextEl.slot] = ss;

    // Inject the DOM panel
    const main = document.getElementById('app-main');
    const prevStep = this.steps[insertIdx - 1];
    const prevPanel = prevStep ? document.getElementById(prevStep.panelId) : null;
    if (main && prevPanel && prevPanel.nextElementSibling) {
      const div = document.createElement('div');
      div.innerHTML = this.renderElementStep(nextEl, panelId);
      const panel = div.firstElementChild;
      main.insertBefore(panel, prevPanel.nextElementSibling);
      this.bindElementStep(nextEl, panelId);
    } else if (main) {
      const scenePanel = this.container.querySelector('[data-step-panel="scene"]');
      if (scenePanel) {
        const div = document.createElement('div');
        div.innerHTML = this.renderElementStep(nextEl, panelId);
        const panel = div.firstElementChild;
        main.insertBefore(panel, scenePanel);
        this.bindElementStep(nextEl, panelId);
      }
    }

    // Navigate to the newly added step
    const newStepIdx = this.steps.findIndex(s => s.slot === nextEl.slot);
    this.rebuildStepperNav();
    if (newStepIdx !== -1) {
      this.goToStep(newStepIdx);
    }
  }

  // Legacy alias
  addNextCharacterStep() { this.addNextElementStep('character'); }

  /** Clear all pending elements (user chose not to add more) */
  _skipRemainingElements() {
    this.pendingCharacterElements = [];
    this.pendingCarElements = [];
  }

  // Legacy alias
  _skipRemainingCharacters() { this._skipRemainingElements(); }

  bindPostAddPopup() {
    const overlay = document.getElementById('post-add-overlay');
    if (!overlay) return;

    document.getElementById('post-add-close').addEventListener('click', () => {
      overlay.style.display = 'none';
    });

    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) overlay.style.display = 'none';
    });

    // "Add Another Design" button
    document.getElementById('post-add-secondary').addEventListener('click', () => {
      overlay.style.display = 'none';
      this.resetForNewDesign();
    });

    // Primary action — mode-dependent, bound dynamically in showPostAddPopup
  }

  showPostAddPopup() {
    const overlay = document.getElementById('post-add-overlay');
    const primaryBtn = document.getElementById('post-add-primary');
    const desc = document.getElementById('post-add-desc');
    const desc2 = document.getElementById('post-add-desc2');

    if (this.state.orderMode === 'convention') {
      desc.textContent = 'Print your order receipt, grab your blank and go to the checkout counter';
      desc2.textContent = 'Create another design to add to cart';
      primaryBtn.textContent = 'Print Order Receipt';
      primaryBtn.onclick = () => {
        this.printConventionReceipt();
      };
    } else {
      desc.textContent = 'Add all items to your Shopify cart for shipping';
      desc2.textContent = 'Create another design to add to cart';
      primaryBtn.textContent = 'Add to Shopify Cart';
      primaryBtn.onclick = () => {
        this.addAllToShopifyCart();
      };
    }

    overlay.style.display = 'flex';
  }

  /** Reset editor state to start a new design (without changing order mode) */
  resetForNewDesign() {
    // Destroy existing creators to release canvas resources
    if (this.slotState) {
      Object.values(this.slotState).forEach(ss => {
        if (ss.creator && typeof ss.creator.destroy === 'function') {
          ss.creator.destroy();
        }
      });
    }

    // Reset design and recipe state
    this.state.designId = null;
    this.state.sceneRecipe = null;
    this.slotState = {};

    // Destroy scene composer
    if (this.sceneComposer) {
      if (typeof this.sceneComposer.destroy === 'function') {
        this.sceneComposer.destroy();
      } else {
        this.sceneComposer.stage.destroy();
      }
      this.sceneComposer = null;
      const sc = document.getElementById('scene-canvas-container');
      if (sc) sc.innerHTML = '';
    }
    this._sceneDesignId = null;
    this._sceneModeKey = null;

    // Clear only notes (preserve customer info for reuse)
    const notesInput = document.getElementById('modal-notes-input');
    if (notesInput) notesInput.value = '';
    const notesCount = document.getElementById('modal-notes-count');
    if (notesCount) notesCount.textContent = '0';

    // Reset product state
    this.state.product.size = 'S';
    this.state.product.color = '#0A0A0A';
    this.state.product.colorName = 'Black';
    this.container.querySelectorAll('#product-size-grid .pd-size-btn').forEach(b => {
      b.classList.toggle('pd-size-btn--active', b.dataset.size === 'S');
    });
    this.container.querySelectorAll('#product-color-grid .pd-color-swatch').forEach(s => {
      s.classList.toggle('pd-color-swatch--active', s.dataset.color === '#0A0A0A');
    });

    // Re-render design grid and navigate to design step
    this.renderDesignGrid();
    this.goToStep(1); // design step
  }

  // ==================== CART VIEWER ====================

  renderCartViewer() {
    return `
      <div class="cart-viewer-overlay" id="cart-viewer-overlay" style="display:none;">
        <div class="cart-viewer-modal">
          <button class="cart-viewer-modal__close" id="cart-viewer-close">&times;</button>
          <h2 class="cart-viewer-modal__title">Your Cart</h2>
          <div id="cart-viewer-content"></div>
        </div>
      </div>`;
  }

  bindCartViewer() {
    const cartBtn = document.getElementById('btn-cart');
    const overlay = document.getElementById('cart-viewer-overlay');
    if (!cartBtn || !overlay) return;

    cartBtn.addEventListener('click', () => {
      this._renderCartViewerContent();
      overlay.style.display = 'flex';
    });

    document.getElementById('cart-viewer-close').addEventListener('click', () => {
      overlay.style.display = 'none';
    });

    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) overlay.style.display = 'none';
    });
  }

  _renderCartViewerContent() {
    const content = document.getElementById('cart-viewer-content');
    if (!content) return;

    if (this.conventionCart.length === 0) {
      content.innerHTML = '<div class="cart-viewer__empty">Your cart is empty.<br>Create a design and add it to your cart!</div>';
      return;
    }

    const itemsHtml = this.conventionCart.map(item => {
      const design = DESIGN_TEMPLATES.find(d => d.id === item.designId);
      const designName = design ? design.name : item.designId || 'Custom Design';
      const previewImg = item.previewThumbnail
        ? `<img src="${item.previewThumbnail}" alt="Preview">`
        : '';
      const productTypeName = item.product.type === 'tshirt' ? 'T-Shirt' : (item.product.type === 'hoodie' ? 'Hoodie' : item.product.type);
      return `
        <div class="cart-viewer__item" data-cart-item-id="${item.id}">
          <div class="cart-viewer__item-preview">${previewImg}</div>
          <div class="cart-viewer__item-info">
            <div class="cart-viewer__item-design">${designName}</div>
            <div class="cart-viewer__item-details">
              Type: ${productTypeName} · Size: ${item.product.size} · Color: ${item.product.colorName}
            </div>
          </div>
          <button class="cart-viewer__item-remove" data-remove-id="${item.id}" title="Remove">&times;</button>
        </div>`;
    }).join('');

    // Show print receipt button only in convention mode
    const printBtn = this.state.orderMode === 'convention'
      ? `<button class="cart-viewer__action-btn cart-viewer__action-btn--primary" id="cart-viewer-print">Print Order Receipt</button>`
      : `<button class="cart-viewer__action-btn cart-viewer__action-btn--primary" id="cart-viewer-shopify">Add to Shopify Cart</button>`;

    content.innerHTML = `
      <div class="cart-viewer__items">${itemsHtml}</div>
      <div class="cart-viewer__actions">
        ${printBtn}
        <div class="cart-viewer__actions-row">
          <button class="cart-viewer__clear-btn" id="cart-viewer-clear">Clear All</button>
        </div>
      </div>`;

    // Bind remove buttons
    content.querySelectorAll('[data-remove-id]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const id = e.currentTarget.dataset.removeId;
        this.removeFromConventionCart(id);
        this._renderCartViewerContent();
      });
    });

    // Bind clear button
    const clearBtn = document.getElementById('cart-viewer-clear');
    if (clearBtn) {
      clearBtn.addEventListener('click', () => {
        this.clearConventionCart();
        this._renderCartViewerContent();
      });
    }

    // Bind print receipt button
    const printBtnEl = document.getElementById('cart-viewer-print');
    if (printBtnEl) {
      printBtnEl.addEventListener('click', () => {
        this.printConventionReceipt();
      });
    }

    // Bind Shopify cart button
    const shopifyBtnEl = document.getElementById('cart-viewer-shopify');
    if (shopifyBtnEl) {
      shopifyBtnEl.addEventListener('click', () => {
        this.addAllToShopifyCart();
        document.getElementById('cart-viewer-overlay').style.display = 'none';
      });
    }
  }
}
Object.assign(DreamMeeApp.prototype, {
  // ==================== PER-SLOT GALLERY RENDERING ====================

  /** Render premade character gallery into the given slot's grid */
  renderCharacterGalleryForSlot(slot) {
    if (typeof PREMADE_CHARACTERS === 'undefined') return;
    const prefix = this.getSlotPrefix(slot);
    const grid = document.getElementById(`${prefix}-premade-grid`);
    if (!grid) return;
    const ss = this.slotState[slot];

    grid.innerHTML = PREMADE_CHARACTERS.map(c => `
      <div class="premade-card ${ss.premadeCharId === c.id ? 'premade-card--selected' : ''}"
           data-premade-char-id="${c.id}">
        <div class="premade-card__preview">
          <img src="${resolveAssetUrl(c.thumb)}" alt="${c.name}" width="400" height="343">
        </div>
      </div>
    `).join('');

    grid.querySelectorAll('[data-premade-char-id]').forEach(card => {
      card.addEventListener('click', e => {
        const id = e.currentTarget.dataset.premadeCharId;
        ss.premadeCharId = id;
        grid.querySelectorAll('.premade-card').forEach(c => c.classList.remove('premade-card--selected'));
        e.currentTarget.classList.add('premade-card--selected');
        const charData = PREMADE_CHARACTERS.find(c => c.id === id);
        if (charData) {
          document.getElementById(`${prefix}-premade-preview`).innerHTML =
            `<div class="dm-spinner"></div><img src="${resolveAssetUrl(charData.src)}" alt="${charData.name}" style="opacity: 0; transition: opacity 0.3s ease" onload="this.style.opacity=1; this.previousElementSibling?.remove()">`;
        }
      });
    });

    // If a default character is pre-selected, show its preview and scroll it into view
    if (ss.premadeCharId) {
      const charData = PREMADE_CHARACTERS.find(c => c.id === ss.premadeCharId);
      if (charData) {
        const preview = document.getElementById(`${prefix}-premade-preview`);
        if (preview) {
          preview.innerHTML = `<div class="dm-spinner"></div><img src="${resolveAssetUrl(charData.src)}" alt="${charData.name}" style="opacity: 0; transition: opacity 0.3s ease" onload="this.style.opacity=1; this.previousElementSibling?.remove()">`;
        }
        // Scroll the selected card into view
        const selectedCard = grid.querySelector(`[data-premade-char-id="${ss.premadeCharId}"]`);
        if (selectedCard) {
          requestAnimationFrame(() => {
            selectedCard.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
          });
        }
      }
    }
  },

  /** Render premade car gallery into the given slot's grid */
  renderCarGalleryForSlot(slot) {
    if (typeof PREMADE_CARS === 'undefined') return;
    const prefix = this.getSlotPrefix(slot);
    const grid = document.getElementById(`${prefix}-premade-grid`);
    if (!grid) return;

    // Populate brand select if present and not already populated
    const select = document.getElementById(`${prefix}-brand-select`);
    if (select && select.options.length <= 1) {
      const brands = [...new Set(PREMADE_CARS.map(c => c.brand).filter(Boolean))].sort();
      brands.forEach(brand => {
        const opt = document.createElement('option');
        opt.value = brand;
        opt.textContent = brand;
        select.appendChild(opt);
      });

      select.addEventListener('change', () => {
        this.filterCarGallery(slot, select.value);
      });
    }

    const selectedBrand = select ? select.value : 'all';
    this.filterCarGallery(slot, selectedBrand);
  },

  filterCarGallery(slot, brand) {
    const prefix = this.getSlotPrefix(slot);
    const grid = document.getElementById(`${prefix}-premade-grid`);
    if (!grid) return;
    const ss = this.slotState[slot];

    const filteredCars = brand === 'all' 
      ? PREMADE_CARS 
      : PREMADE_CARS.filter(c => c.brand === brand);

    grid.innerHTML = filteredCars.map(model => `
      <div class="premade-card ${ss.premadeCarModelId === model.id ? 'premade-card--selected' : ''}"
           data-premade-car-model="${model.id}">
        <div class="premade-card__preview">
          <img src="${resolveAssetUrl(model.variants[0].thumb)}" alt="${model.name}" width="400" height="343">
        </div>
      </div>
    `).join('');

    grid.querySelectorAll('[data-premade-car-model]').forEach(card => {
      card.addEventListener('click', e => {
        const modelId = e.currentTarget.dataset.premadeCarModel;
        ss.premadeCarModelId = modelId;
        grid.querySelectorAll('.premade-card').forEach(c => c.classList.remove('premade-card--selected'));
        e.currentTarget.classList.add('premade-card--selected');

        const model = PREMADE_CARS.find(m => m.id === modelId);
        if (model) {
          ss.premadeCarVariantId = model.variants[0].id;
          this.renderCarVariantStripForSlot(slot, model);
          this.updateCarPreviewForSlot(slot, model.variants[0]);
        }
      });
    });

    // If a default car is pre-selected, show its preview and scroll into view
    if (ss.premadeCarModelId || ss.premadeCarVariantId) {
      // Find the model that contains the pre-selected variant
      let preSelectedModel = null;
      let preSelectedVariant = null;
      if (ss.premadeCarModelId) {
        preSelectedModel = PREMADE_CARS.find(m => m.id === ss.premadeCarModelId);
      }
      if (!preSelectedModel && ss.premadeCarVariantId) {
        // Find model by variant ID
        for (const model of PREMADE_CARS) {
          const v = model.variants.find(v => v.id === ss.premadeCarVariantId);
          if (v) {
            preSelectedModel = model;
            preSelectedVariant = v;
            ss.premadeCarModelId = model.id;
            break;
          }
        }
      }
      if (preSelectedModel) {
        if (!preSelectedVariant) {
          preSelectedVariant = preSelectedModel.variants.find(v => v.id === ss.premadeCarVariantId) || preSelectedModel.variants[0];
          ss.premadeCarVariantId = preSelectedVariant.id;
        }
        // Mark the model card as selected
        const selectedCard = grid.querySelector(`[data-premade-car-model="${preSelectedModel.id}"]`);
        if (selectedCard) {
          grid.querySelectorAll('.premade-card').forEach(c => c.classList.remove('premade-card--selected'));
          selectedCard.classList.add('premade-card--selected');
          requestAnimationFrame(() => {
            selectedCard.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
          });
        }
        this.renderCarVariantStripForSlot(slot, preSelectedModel);
        this.updateCarPreviewForSlot(slot, preSelectedVariant);
      }
    }
  },

  renderCarVariantStripForSlot(slot, model) {
    const prefix = this.getSlotPrefix(slot);
    const section = document.getElementById(`${prefix}-variant-section`);
    if (!section) return;
    const ss = this.slotState[slot];

    if (model.variants.length <= 1) {
      section.innerHTML = '';
      return;
    }

    section.innerHTML = `
      <div class="variant-section">
        <div class="variant-section__title">Variants for ${model.name}</div>
        <div class="variant-strip">
          ${model.variants.map(v => `
            <div class="variant-thumb ${ss.premadeCarVariantId === v.id ? 'variant-thumb--selected' : ''}"
                 data-variant-id="${v.id}">
              <img src="${resolveAssetUrl(v.thumb)}" alt="${v.name}" width="400" height="343">
            </div>
          `).join('')}
        </div>
      </div>
    `;

    section.querySelectorAll('[data-variant-id]').forEach(thumb => {
      thumb.addEventListener('click', e => {
        const variantId = e.currentTarget.dataset.variantId;
        ss.premadeCarVariantId = variantId;
        section.querySelectorAll('.variant-thumb').forEach(t => t.classList.remove('variant-thumb--selected'));
        e.currentTarget.classList.add('variant-thumb--selected');
        const variant = model.variants.find(v => v.id === variantId);
        if (variant) this.updateCarPreviewForSlot(slot, variant);
      });
    });
  },

  updateCarPreviewForSlot(slot, variant) {
    const prefix = this.getSlotPrefix(slot);
    const preview = document.getElementById(`${prefix}-premade-preview`);
    if (preview && variant) {
      preview.innerHTML = `<div class="dm-spinner"></div><img src="${resolveAssetUrl(variant.src)}" alt="${variant.name}" style="opacity: 0; transition: opacity 0.3s ease" onload="this.style.opacity=1; this.previousElementSibling?.remove()">`;
    }
  }
});
Object.assign(DreamMeeApp.prototype, {
  // ==================== STEP 1: Design Selection ====================

  bindStep1() {
    this.renderDesignGrid();

    document.getElementById('step1-next').addEventListener('click', () => {
      if (!this.state.designId) {
        this.showToast('Please select a design first');
        return;
      }
      this.goToStep(this.currentStepIdx + 1);
    });
  },

  /** Populate the product detail step with current design info */
  _populateProductStep() {
    const design = this.getSelectedDesign();
    const titleEl = document.getElementById('product-detail-title');
    const imgEl = document.getElementById('product-detail-img');
    const isHoodie = this.state.product.type === 'hoodie';
    const productLabel = isHoodie ? 'Hoodie' : 'T-Shirt';
    if (design) {
      if (titleEl) titleEl.textContent = `${design.name || 'Custom'} (${productLabel})`;
      if (imgEl) imgEl.src = resolveAssetUrl(design.thumbnail);
    }

    // Dynamic product description from container data attribute
    const descEl = document.getElementById('product-detail-desc');
    if (descEl) {
      const rawDesc = this.container.getAttribute('data-product-description') || '';
      let shopifyDescHtml;
      if (rawDesc) {
        shopifyDescHtml = rawDesc;
      } else {
        shopifyDescHtml = isHoodie ? `
          <p>Premium Heavyweight Cotton Hoodie</p>
          <p>Sizing Chart (Or Ask A Staff Member)</p>
        ` : `
          <p>100% Cotton Oversized US Fit</p>
          <p>Sizing Chart (Or Ask A Staff Member)</p>
        `;
      }

      // Dynamic Includes based on selected design template
      let includesHtml = '';
      if (design && design.elements) {
        const charCount = design.elements.filter(e => e.type === 'character').length;
        const carCount = design.elements.filter(e => e.type === 'car').length;
        const includesList = [];
        if (charCount > 0) {
          includesList.push(`Up To ${charCount} Character${charCount > 1 ? 's' : ''}`);
        }
        if (carCount > 0) {
          includesList.push(`Up To ${carCount} Car${carCount > 1 ? 's' : ''}`);
        }
        includesList.push('Misc. Accessories');
        
        includesHtml = `
          <p>Includes:</p>
          ${includesList.map(item => `<p>${item}</p>`).join('')}
        `;
      } else {
        includesHtml = `
          <p>Includes:</p>
          <p>Up To 2 Characters</p>
          <p>Up To 1 Car</p>
          <p>Misc. Accessories</p>
        `;
      }

      descEl.innerHTML = `
        ${shopifyDescHtml}
        <div class="product-detail__includes" style="margin-top: 16px;">
          ${includesHtml}
        </div>
      `;
    }
  },

  // ==================== STEP 5: Product + Checkout ====================

  bindStep5() {
    // Type select dropdown
    const typeSelect = document.getElementById('checkout-type-select');
    if (typeSelect) {
      typeSelect.addEventListener('change', (e) => {
        this.state.product.type = e.target.value;
        this.syncShirtOptions();
        this._populateProductStep();
      });
    }

    // Size select dropdown
    const sizeSelect = document.getElementById('checkout-size-select');
    if (sizeSelect) {
      sizeSelect.addEventListener('change', (e) => {
        this.state.product.size = e.target.value;
        this.syncShirtOptions();
      });
    }

    // Color select dropdown
    const colorSelect = document.getElementById('checkout-color-select');
    if (colorSelect) {
      colorSelect.addEventListener('change', (e) => {
        const selectedOption = e.target.options[e.target.selectedIndex];
        this.state.product.color = e.target.value;
        this.state.product.colorName = selectedOption.dataset.colorName;
        this.syncShirtOptions();
      });
    }
  },

  /** Reverse-sync product step size from checkout */
  _syncProductSize() {
    this.syncShirtOptions();
  },

  /** Reverse-sync product step color from checkout */
  _syncProductColor() {
    this.syncShirtOptions();
  },

  /** Forward-sync checkout selects from product step state */
  _syncCheckoutSelects() {
    this.syncShirtOptions();
  },

  renderDesignGrid() {
    const grid = document.getElementById('design-grid');
    if (!grid) return;

    grid.innerHTML = DESIGN_TEMPLATES.map(design => `
      <div class="design-card ${this.state.designId === design.id ? 'design-card--selected' : ''}"
           data-design-id="${design.id}">
        <div class="design-card__preview">
          <img src="${resolveAssetUrl(design.thumbnail)}" alt="${design.name}" loading="lazy">
        </div>
        <div class="design-card__info">
          <h3 class="design-card__name">${design.name}</h3>
        </div>
      </div>
    `).join('');

    grid.querySelectorAll('[data-design-id]').forEach(card => {
      card.addEventListener('click', e => {
        const id = e.currentTarget.dataset.designId;
        this.state.designId = id;
        grid.querySelectorAll('.design-card').forEach(c => c.classList.remove('design-card--selected'));
        e.currentTarget.classList.add('design-card--selected');

        // Rebuild dynamic steps and stepper for the new design
        this.rebuildSteps();
      });

      card.addEventListener('dblclick', e => {
        const id = e.currentTarget.dataset.designId;
        this.state.designId = id;
        grid.querySelectorAll('.design-card').forEach(c => c.classList.remove('design-card--selected'));
        e.currentTarget.classList.add('design-card--selected');
        this.rebuildSteps();
        this.goToStep(this.currentStepIdx + 1);
      });
    });
  },

  getSelectedDesign() {
    return DESIGN_TEMPLATES.find(d => d.id === this.state.designId) || null;
  },

  // ==================== SCENE & CHECKOUT NAV ====================

  bindSceneNav() {
    const btnBack = document.getElementById('step4-back');
    if (btnBack) {
      btnBack.addEventListener('click', () => this.goToStep(this.currentStepIdx - 1));
    }
    document.getElementById('step4-next').addEventListener('click', () => this.goToStep(this.currentStepIdx + 1));
    const step5Back = document.getElementById('step5-back');
    if (step5Back) step5Back.addEventListener('click', () => this.goToStep(this.currentStepIdx - 1));
  },

  // ==================== NAVIGATION ====================

  async goToStep(targetIdx) {
    if (targetIdx < 0 || targetIdx >= this.steps.length) return;

    // Hide current step panel using panelId
    const curStep = this.steps[this.currentStepIdx];
    const curPanel = document.getElementById(curStep.panelId || `step-${curStep.id}`);
    if (curPanel) curPanel.classList.add('step-panel--hidden');

    const targetStep = this.steps[targetIdx];

    // --- Product step: populate design info ---
    if (targetStep.type === 'product') {
      this._populateProductStep();
    }

    // --- Scene step: build/refresh SceneComposer with per-slot data ---
    if (targetStep.type === 'scene') {
      this._enterSceneStep();
    }

    // --- Checkout step ---
    if (targetStep.type === 'checkout') {
      await this.prepareCheckout();
    }

    // Show target panel using panelId
    const panel = document.getElementById(targetStep.panelId || `step-${targetStep.id}`);
    if (panel) {
      panel.classList.remove('step-panel--hidden');
      panel.classList.remove('step-panel');
      void panel.offsetWidth;
      panel.classList.add('step-panel');
    }

    // Deferred gallery rendering for element steps (iOS Safari grid-inside-hidden fix)
    if (targetStep.slot) {
      const ss = this.slotState[targetStep.slot];
      if (ss && !ss.galleryRendered) {
        ss.galleryRendered = true;
        if (targetStep.type === 'character') {
          this.renderCharacterGalleryForSlot(targetStep.slot);
        } else if (targetStep.type === 'car') {
          this.renderCarGalleryForSlot(targetStep.slot);
        }
      }
      // Resize builder canvas if in builder mode
      if (ss && ss.mode === 'builder' && ss.creator) {
        setTimeout(() => ss.creator.handleResize(), 50);
      }
    }

    this.currentStepIdx = targetIdx;
    // Track the furthest step ever reached — used by stepper nav to allow
    // navigating to any previously-visited step even after going backward.
    if (this._maxStepReached == null || targetIdx > this._maxStepReached) {
      this._maxStepReached = targetIdx;
    }
    this.rebuildStepperNav();

    // Toggle header visibility: hide on design step only (has its own hero banner)
    const header = document.getElementById('app-header');
    if (header) {
      header.classList.toggle('app-header--hidden', targetStep.type === 'design' || targetStep.type === 'mode-select');
    }

    // On mobile, scroll the preview area into view
    if (window.innerWidth <= 600 && panel) {
      requestAnimationFrame(() => {
        const scrollTarget =
          panel.querySelector('.premade-preview') ||
          panel.querySelector('.canvas-wrapper') ||
          panel.querySelector('.checkout-preview') ||
          panel;
        scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }

    if (targetStep.type === 'scene' && this.sceneComposer) {
      setTimeout(() => this.sceneComposer.handleResize(), 50);
      this.showSceneHint();
    }
  },

  // ==================== SCENE STEP LOGIC ====================

  _enterSceneStep() {
    const design = this.getSelectedDesign();
    const elements = design ? getDesignElements(design) : [];

    // Collect per-slot builder creators.
    // SceneComposer expects a single characterCreator and carCreator.
    // For multi-element gallery mode, we set premade sources per-slot via setElementPremade().
    let firstCharCreator = null;
    let firstCarCreator = null;

    for (const el of elements) {
      const ss = this.slotState[el.slot];
      if (!ss) continue;
      if (ss.mode === 'builder' && ss.creator) {
        ss.recipe = ss.creator.generateRecipe();
        if (el.type === 'character' && !firstCharCreator) firstCharCreator = ss.creator;
        if (el.type === 'car' && !firstCarCreator) firstCarCreator = ss.creator;
      }
    }

    // Detect design change → destroy old SceneComposer
    if (this.sceneComposer && this._sceneDesignId !== this.state.designId) {
      if (typeof this.sceneComposer.destroy === 'function') {
        this.sceneComposer.destroy();
      } else {
        this.sceneComposer.stage.destroy();
      }
      this.sceneComposer = null;
      const sceneContainer = document.getElementById('scene-canvas-container');
      if (sceneContainer) sceneContainer.innerHTML = '';
    }

    // Also rebuild if any slot mode changed since last init
    const activeElements = elements.filter(el => this.slotState[el.slot]);
    const modeKey = activeElements.map(el => `${el.slot}:${this.slotState[el.slot]?.mode}`).join(',');
    if (this.sceneComposer && this._sceneModeKey !== modeKey) {
      if (typeof this.sceneComposer.destroy === 'function') {
        this.sceneComposer.destroy();
      } else {
        this.sceneComposer.stage.destroy();
      }
      this.sceneComposer = null;
      const sceneContainer = document.getElementById('scene-canvas-container');
      if (sceneContainer) sceneContainer.innerHTML = '';
    }

    if (!this.sceneComposer) {
      this._sceneDesignId = this.state.designId;
      this._sceneModeKey = modeKey;

      // Create a filtered design that only includes elements with active slotState.
      // This excludes characters the user chose not to add via the progressive popup.
      const filteredDesign = { ...design, elements: activeElements };

      this.sceneComposer = new SceneComposer(
        'scene-canvas-container',
        'scene-controls',
        firstCharCreator,
        firstCarCreator,
        filteredDesign,
        SCENE_CONFIG
      );
      // Register all builder creators with the composer
      for (const el of elements) {
        const ss = this.slotState[el.slot];
        if (ss && ss.mode === 'builder' && ss.creator) {
          this.sceneComposer.setElementCreator(el.slot, ss.creator);
        }
      }
      // Set per-slot premade sources for gallery-mode slots
      for (const el of elements) {
        const ss = this.slotState[el.slot];
        if (!ss || ss.mode !== 'gallery') continue;
        const src = el.type === 'character'
          ? this._getPremadeCharSrcForSlot(el.slot)
          : this._getPremadeCarSrcForSlot(el.slot);
        if (src) this.sceneComposer.setElementPremade(el.slot, src);
      }
    } else {
      // Refresh existing composer with updated per-slot sources
      for (const el of elements) {
        const ss = this.slotState[el.slot];
        if (!ss) continue;
        if (ss.mode === 'builder' && ss.creator) {
          if (el.type === 'character') {
            this.sceneComposer.refreshCharacterSnapshot(ss.creator, el.slot);
          } else {
            this.sceneComposer.refreshCarSnapshot(ss.creator, el.slot);
          }
        } else if (ss.mode === 'gallery') {
          const src = el.type === 'character'
            ? this._getPremadeCharSrcForSlot(el.slot)
            : this._getPremadeCarSrcForSlot(el.slot);
          if (src) this.sceneComposer.setElementPremade(el.slot, src);
        }
      }
    }
  },

  // ==================== PREMADE HELPERS (per-slot) ====================

  _getPremadeCharSrcForSlot(slot) {
    const ss = this.slotState[slot];
    if (!ss || !ss.premadeCharId || typeof PREMADE_CHARACTERS === 'undefined') return null;
    const c = PREMADE_CHARACTERS.find(c => c.id === ss.premadeCharId);
    return c ? c.src : null;
  },

  _getPremadeCarSrcForSlot(slot) {
    const ss = this.slotState[slot];
    if (!ss || !ss.premadeCarVariantId || typeof PREMADE_CARS === 'undefined') return null;
    for (const model of PREMADE_CARS) {
      const v = model.variants.find(v => v.id === ss.premadeCarVariantId);
      if (v) return v.src;
    }
    return null;
  }
});
Object.assign(DreamMeeApp.prototype, {
  // ==================== CHECKOUT ====================

  async prepareCheckout() {
    // Capture per-slot builder recipes
    for (const [slot, ss] of Object.entries(this.slotState)) {
      if (ss.mode === 'builder' && ss.creator) {
        ss.recipe = ss.creator.generateRecipe();
      }
    }

    const originalView = this.sceneComposer ? this.sceneComposer.state.activeView : 'back';
    if (this.sceneComposer) {
      // Temporarily switch to back view before generating recipe and preview to make sure all elements are instantiated and their positions are captured!
      if (this.sceneComposer.hasMultipleViews && originalView !== 'back') {
        this.sceneComposer.state.activeView = 'back';
        await this.sceneComposer.renderScene();
      }
      this.state.sceneRecipe = this.sceneComposer.generateRecipe();
      if (originalView !== 'back') {
        this.state.sceneRecipe.activeView = originalView;
      }
    }

    // Generate preview
    let previewDataUrl = null;
    this._previewThumbnail = null;
    if (this.sceneComposer && this.sceneComposer.stage) {
      this.sceneComposer.transformer.nodes([]);
      this.sceneComposer.transformerLayer.batchDraw();

      previewDataUrl = this.sceneComposer.stage.toDataURL({ pixelRatio: 2 });
      this._previewThumbnail = this.generateThumbnailFromStage(
        this.sceneComposer.stage, 200, 244, 0.82
      );

      // Restore original view
      if (this.sceneComposer.hasMultipleViews && originalView !== 'back') {
        this.sceneComposer.state.activeView = originalView;
        await this.sceneComposer.renderScene();
      }
    }

    // Show preview in checkout step
    const preview = document.getElementById('checkout-preview');
    if (preview) {
      if (previewDataUrl) {
        preview.innerHTML = `<img src="${previewDataUrl}" alt="Design Preview">`;
      } else {
        preview.innerHTML = '<p style="padding:40px;text-align:center;color:var(--dm-text-muted);">No preview available</p>';
      }
    }

    // Render summary text below preview
    this._renderCheckoutSummary();

    // Sync checkout selects from product state
    this.syncShirtOptions();

    // Show/hide convention-only fields depending on orderMode
    const isShipping = this.state.orderMode === 'shipping';

    const fullnameInput = document.getElementById('checkout-fullname');
    const phoneInput = document.getElementById('checkout-phone');
    const addressInput = document.getElementById('checkout-address');

    if (fullnameInput) {
      const fieldSection = fullnameInput.closest('.checkout-info-section');
      if (fieldSection) fieldSection.style.display = isShipping ? 'none' : 'block';
      fullnameInput.required = !isShipping;
    }
    if (phoneInput) {
      const fieldSection = phoneInput.closest('.checkout-info-section');
      if (fieldSection) fieldSection.style.display = isShipping ? 'none' : 'block';
      phoneInput.required = !isShipping;
    }
    if (addressInput) {
      const fieldSection = addressInput.closest('.checkout-info-section');
      if (fieldSection) fieldSection.style.display = isShipping ? 'none' : 'block';
      addressInput.required = !isShipping;
    }

    // Build and store the recipe
    const fullRecipe = this.buildFullRecipe();
    const recipeJson = document.getElementById('modal-recipe-json');
    if (recipeJson) recipeJson.textContent = JSON.stringify(fullRecipe, null, 2);
    const recipeDisplay = document.getElementById('modal-recipe-display');
    if (recipeDisplay) recipeDisplay.style.display = 'none';

    console.log('Full Recipe:', fullRecipe);
  },

  buildFullRecipe() {
    const design = this.getSelectedDesign();
    const elements = design ? getDesignElements(design) : [];

    const elementRecipes = {};
    for (const el of elements) {
      const ss = this.slotState[el.slot];
      if (!ss) continue;

      if (ss.mode === 'gallery') {
        if (el.type === 'character' && ss.premadeCharId) {
          const charData = (typeof PREMADE_CHARACTERS !== 'undefined') ? PREMADE_CHARACTERS.find(c => c.id === ss.premadeCharId) : null;
          elementRecipes[el.slot] = { type: 'premade', elementType: el.type, premadeId: ss.premadeCharId, premadeSrc: charData ? charData.src : null };
        } else if (el.type === 'car' && ss.premadeCarVariantId) {
          elementRecipes[el.slot] = { type: 'premade', elementType: el.type, premadeId: ss.premadeCarVariantId, premadeModelId: ss.premadeCarModelId, premadeSrc: this._getPremadeCarSrcForSlot(el.slot) };
        }
      } else if (ss.recipe) {
        elementRecipes[el.slot] = { ...ss.recipe, elementType: el.type };
      }
    }

    const fullRecipe = {
      product: { ...this.state.product },
      designId: this.state.designId,
      elements: elementRecipes,
      scene: this.state.sceneRecipe,
      timestamp: new Date().toISOString(),
      version: '3.0.0'
    };

    return fullRecipe;
  },

  /** Render the summary text block below the preview image */
  _renderCheckoutSummary() {
    const summary = document.getElementById('checkout-summary');
    if (!summary) return;

    const design = this.getSelectedDesign();
    const elements = design ? getDesignElements(design) : [];
    let summaryLines = [];
    const isHoodie = this.state.product.type === 'hoodie';
    const label = isHoodie ? 'Hoodie' : 'T-Shirt';
    summaryLines.push(`Size: US Universal Fit ${this.state.product.size}`);
    summaryLines.push(`${label} Color: ${this.state.product.colorName}`);
    if (design) summaryLines.push(`Background: ${design.name}`);
    for (const el of elements) {
      const ss = this.slotState[el.slot];
      if (!ss) continue;
      const label = el.type === 'character' ? `Character #${el.slot.split('_')[1] || '1'}` : `Car`;
      if (ss.mode === 'gallery') {
        if (el.type === 'character' && ss.premadeCharId) {
          summaryLines.push(`${label}: Pre-Made`);
        } else if (el.type === 'car' && ss.premadeCarModelId) {
          summaryLines.push(`${label}: Pre-Made`);
        }
      } else {
        summaryLines.push(`${label}: Custom`);
      }
    }
    if (this.state.sceneRecipe && this.state.sceneRecipe.text) {
      const textContent = typeof this.state.sceneRecipe.text === 'string'
        ? this.state.sceneRecipe.text
        : this.state.sceneRecipe.text.text;
      if (textContent && textContent.trim()) {
        summaryLines.push(`Text (if applicable): "${textContent}"`);
      }
    }
    summary.innerHTML = `<div class="checkout-summary__title">SUMMARY:</div>` +
      summaryLines.map(l => `<div class="checkout-summary__line">${l}</div>`).join('');
  },

  bindCheckoutActions() {
    const notesToggle = document.getElementById('modal-notes-toggle');
    const notesSection = document.getElementById('modal-notes-section');
    const notesInput = document.getElementById('modal-notes-input');
    const notesCount = document.getElementById('modal-notes-count');

    notesToggle.addEventListener('click', () => {
      const isOpen = notesSection.classList.toggle('modal__notes--open');
      if (isOpen) setTimeout(() => notesInput.focus(), 200);
    });

    notesInput.addEventListener('input', () => {
      const sanitized = this.sanitizeNotes(notesInput.value);
      if (sanitized !== notesInput.value) {
        const cursorPos = notesInput.selectionStart;
        const diff = notesInput.value.length - sanitized.length;
        notesInput.value = sanitized;
        notesInput.setSelectionRange(cursorPos - diff, cursorPos - diff);
      }
      notesCount.textContent = notesInput.value.length;
    });

    document.getElementById('modal-add-to-cart').addEventListener('click', () => this.addToCart());

    // Booth submit — only bind if the element exists (it's removed in the new layout)
    const boothBtn = document.getElementById('modal-booth-submit');
    if (boothBtn) boothBtn.addEventListener('click', () => this.submitBoothOrder());

    document.getElementById('modal-download').addEventListener('click', () => {
      const img = document.querySelector('#checkout-preview img');
      if (!img) return;
      const link = document.createElement('a');
      link.download = `dreammee-design-${Date.now()}.png`;
      link.href = img.src;
      link.click();
      this.showToast('PNG downloaded!');
    });

    document.getElementById('modal-recipe').addEventListener('click', () => {
      const display = document.getElementById('modal-recipe-display');
      const jsonEl = document.getElementById('modal-recipe-json');
      const notes = this.sanitizeNotes((document.getElementById('modal-notes-input') || {}).value || '');
      const recipe = this.buildFullRecipe();
      if (notes) { recipe.customerNotes = notes; } else { delete recipe.customerNotes; }
      jsonEl.textContent = JSON.stringify(recipe, null, 2);
      display.style.display = display.style.display === 'none' ? 'block' : 'none';
      if (display.style.display === 'block') {
        navigator.clipboard.writeText(jsonEl.textContent).then(() => this.showToast('Recipe JSON copied to clipboard!')).catch(() => this.showToast('Recipe JSON shown below.'));
      }
    });
  },

  // ==================== ADD TO CONVENTION CART ====================

  async addToCart() {
    const btn = document.getElementById('modal-add-to-cart');

    // Validate required fields
    const fullnameInput = document.getElementById('checkout-fullname');
    const phoneInput = document.getElementById('checkout-phone');
    const addressInput = document.getElementById('checkout-address');

    const fullname = (fullnameInput?.value || '').trim();
    const phone = (phoneInput?.value || '').trim();
    const address = (addressInput?.value || '').trim();

    // Clear previous errors
    [fullnameInput, phoneInput, addressInput].forEach(el => {
      if (el) el.classList.remove('checkout-field__input--error');
    });

    const isShipping = this.state.orderMode === 'shipping';

    if (!isShipping) {
      if (!fullname) {
        fullnameInput?.classList.add('checkout-field__input--error');
        fullnameInput?.focus();
        this.showToast('Please enter your full name');
        return;
      }
      if (!phone) {
        phoneInput?.classList.add('checkout-field__input--error');
        phoneInput?.focus();
        this.showToast('Please enter your phone number');
        return;
      }
      if (!address) {
        addressInput?.classList.add('checkout-field__input--error');
        addressInput?.focus();
        this.showToast('Please enter your address');
        return;
      }
    }

    // Build recipe
    const customerNotes = this.sanitizeNotes(
      (document.getElementById('modal-notes-input') || {}).value || ''
    );
    const recipe = this.buildFullRecipe();
    if (customerNotes) { recipe.customerNotes = customerNotes; } else { delete recipe.customerNotes; }

    // Regenerate preview thumbnail right before adding to cart to ensure it is fully loaded and captures final positions
    if (this.sceneComposer && this.sceneComposer.stage) {
      this.sceneComposer.transformer.nodes([]);
      this.sceneComposer.transformerLayer.batchDraw();

      // Temporarily switch to back view to capture the customized print preview
      const originalView = this.sceneComposer.state.activeView;
      if (this.sceneComposer.hasMultipleViews && originalView !== 'back') {
        this.sceneComposer.state.activeView = 'back';
        await this.sceneComposer.renderScene();
      }

      this._previewThumbnail = this.generateThumbnailFromStage(
        this.sceneComposer.stage, 200, 244, 0.82
      );

      // Restore original view
      if (this.sceneComposer.hasMultipleViews && originalView !== 'back') {
        this.sceneComposer.state.activeView = originalView;
        await this.sceneComposer.renderScene();
      }
    }

    // Build cart item
    const smsConsentInput = document.getElementById('checkout-sms-consent');
    const smsConsent = smsConsentInput ? smsConsentInput.checked : false;

    const cartItem = {
      id: `dm_${Date.now()}`,
      recipe,
      product: { ...this.state.product },
      designId: this.state.designId,
      previewThumbnail: this._previewThumbnail || '',
      customerNotes,
      customerName: isShipping ? '' : fullname,
      phone: isShipping ? '' : phone,
      address: isShipping ? '' : address,
      smsConsent: isShipping ? false : smsConsent,
      timestamp: new Date().toISOString()
    };

    // Add to convention cart
    this.addToConventionCart(cartItem);

    btn.textContent = '✅ Added!';
    btn.disabled = true;
    this.showToast('Design added to cart!');

    // Show post-add popup after brief delay
    setTimeout(() => {
      btn.textContent = 'Add to Cart';
      btn.disabled = false;
      this.showPostAddPopup();
    }, 600);
  },

  // ==================== ADD ALL TO SHOPIFY CART (Shipping mode) ====================

  async addAllToShopifyCart() {
    const primaryBtn = document.getElementById('post-add-primary');

    const variantId = this.getShopifyVariantId();
    if (!variantId) {
      this.showToast('Running outside Shopify — cannot add to Shopify cart');
      console.log('[DreamMee] Standalone mode — convention cart items:', this.conventionCart);
      return;
    }

    primaryBtn.disabled = true;
    primaryBtn.textContent = '⏳ Adding...';

    let successCount = 0;
    for (const item of this.conventionCart) {
      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({
            id: variantId,
            quantity: 1,
            properties: {
              '_dreammee_recipe': JSON.stringify(item.recipe, null, 2),
              '_dreammee_preview': item.previewThumbnail || '',
              '_dreammee_type': item.product.type || 'tshirt',
              '_dreammee_size': item.product.size,
              '_dreammee_color': item.product.colorName,
              '_dreammee_design': item.designId,
              '_dreammee_notes': item.customerNotes || '',
              '_dreammee_fullname': item.customerName || '',
              '_dreammee_phone': item.phone || '',
              '_dreammee_address': item.address || '',
              '_dreammee_sms_consent': item.smsConsent ? 'Yes' : 'No'
            }
          })
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        successCount++;
      } catch (err) {
        console.error('[DreamMee] Failed to add item to Shopify cart:', err);
      }
    }

    if (successCount > 0) {
      this.showToast(`${successCount} item(s) added to Shopify cart!`);
      this.clearConventionCart();
      if (typeof window.refreshCart === 'function') window.refreshCart();
      setTimeout(() => { window.location.href = '/cart'; }, 1500);
    } else {
      this.showToast('Failed to add items to cart. Please try again.');
      primaryBtn.disabled = false;
      primaryBtn.textContent = 'Add to Shopify Cart';
    }
  },

  // ==================== CONVENTION RECEIPT (All items) ====================

  /** Detect if the current device is an iPad (or iPhone) running iPadOS/iOS */
  _isIPadOS() {
    if (!navigator) return false;
    // iPadOS 13+ reports as "Macintosh" in the UA but has touch support
    const isMacTouch = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    return isMacTouch || isIOS;
  },

  /**
   * Send HTML receipt content to the Star PassPRNT companion app via URL scheme.
   * The printer auto-cuts after each job via the `cut=partial` parameter.
   * @param {string} htmlContent - Clean HTML code representing the receipt.
   */
  _printViaPassPRNT(htmlContent) {
    const baseUri = 'starpassprnt://v1/print/nopreview?';
    //const baseUri = 'starpassprnt://v1/print/?';
    const params = [];

    // Tell PassPRNT where to redirect back when finished
    const backUrl = window.location.href;
    params.push('back=' + encodeURIComponent(backUrl));

    // Add the URL-encoded HTML print payload
    params.push('html=' + encodeURIComponent(htmlContent));

    // Force the paper width size (2inch = 58mm)
    params.push('size=2');

    // Auto-cut paper after printing (partial cut leaves a small tab)
    params.push('cut=partial');

    // Combine and redirect
    const fullUrl = baseUri + params.join('&');
    console.log('[DreamMee] PassPRNT URL length:', fullUrl.length, 'chars');
    window.location.href = fullUrl;
  },

  /**
   * Build a compact receipt HTML string (no preview images, no base64 data).
   * Used for both PassPRNT and the browser print window.
   */
  _buildReceiptHtml({ cart, orderId, customerName, phone }) {
    const now = new Date().toLocaleString();

    const itemRows = cart.map((item, i) => {
      const design = typeof DESIGN_TEMPLATES !== 'undefined'
        ? DESIGN_TEMPLATES.find(d => d.id === item.designId)
        : null;
      const designName = design ? design.name : item.designId || '—';
      // NOTE: Preview images intentionally omitted from print to reduce URL length.
      // Thumbnails are still saved on the server via the API.
      const notesHtml = item.customerNotes
        ? `<div class="notes">📝 ${item.customerNotes}</div>`
        : '';
      const typeLabel = item.product.type === 'hoodie' ? 'Hoodie' : 'T-Shirt';
      return `
        <div class="divider"></div>
        <div class="item-header">Item ${i + 1}</div>
        <div class="field"><span class="label">Product:</span><span class="value">${typeLabel}</span></div>
        <div class="field"><span class="label">Design:</span><span class="value">${designName}</span></div>
        <div class="field"><span class="label">Size:</span><span class="value">${item.product.size}</span></div>
        <div class="field"><span class="label">Color:</span><span class="value">${item.product.colorName}</span></div>
        ${notesHtml}`;
    }).join('');

    const orderIdHtml = orderId
      ? `<div class="order-id">${orderId}</div>`
      : '';

    // Build a single copy of the receipt body
    const receiptBody = `
  <div class="header">DreamMee</div>
  ${orderIdHtml}
  <div class="item-count">${cart.length} Item${cart.length > 1 ? 's' : ''}</div>
  <div class="divider"></div>
  <div class="field"><span class="label">Phone:</span><span class="value">${phone}</span></div>
  <div class="field"><span class="label">Name:</span><span class="value">${customerName}</span></div>
  ${itemRows}
  <div class="divider"></div>
  <div class="field"><span class="label">Time:</span><span class="value">${now}</span></div>
  <div class="call-note">Show this receipt on pickup</div>
  <div class="footer">Thank you!</div>`;

    // Print two copies: one for shop, one for customer
    return `<!DOCTYPE html>
<html><head><meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<meta name="format-detection" content="telephone=no">
<title>DreamMee Receipt${orderId ? ' — ' + orderId : ''}</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: 'Arial', 'Helvetica', sans-serif; width: 100%; padding: 8px; color: #000; background: #fff; }
  .header { text-align: center; font-size: 28px; font-weight: bold; padding: 6px 0; border-bottom: 3px double #000; }
  .order-id { text-align: center; font-size: 36px; font-weight: 900; padding: 8px 0; letter-spacing: 2px; border: 3px solid #000; margin: 6px 0; }
  .divider { border-top: 2px dashed #000; margin: 6px 0; }
  .item-count { text-align: center; font-size: 22px; font-weight: bold; padding: 4px 0; }
  .field { padding: 3px 0; font-size: 18px; }
  .field .label { font-weight: bold; }
  .field .value { display: block; font-size: 20px; padding-left: 2px; word-break: break-word; }
  .item-header { font-size: 20px; font-weight: bold; padding: 6px 0 2px; text-align: center; border: 1px solid #000; }
  .notes { font-size: 16px; padding: 4px; margin: 4px 0; white-space: pre-wrap; word-break: break-word; border: 1px solid #000; }
  .call-note { text-align: center; font-size: 22px; font-weight: 900; padding: 8px 4px; background: #000; color: #fff; margin: 8px 0 4px; }
  .footer { text-align: center; font-size: 14px; padding: 4px 0; border-top: 3px double #000; }
  .copy-label { text-align: center; font-size: 16px; font-weight: bold; padding: 4px 0; text-transform: uppercase; letter-spacing: 1px; }
  .cut-line { border-top: 3px dashed #000; margin: 16px 0; text-align: center; position: relative; }
  .cut-line::after { content: '✂ CUT HERE'; position: relative; top: -10px; background: #fff; padding: 0 8px; font-size: 12px; font-weight: bold; letter-spacing: 1px; }
  @media print { @page { size: 58mm auto; margin: 0; } }
</style>
</head><body>
  <div class="copy-label">— SHOP COPY —</div>
  ${receiptBody}
  <div class="cut-line"></div>
  <div class="copy-label">— CUSTOMER COPY —</div>
  ${receiptBody}
</body></html>`;
  },

  async printConventionReceipt() {
    const cart = this.conventionCart;
    if (cart.length === 0) {
      this.showToast('Cart is empty!');
      return;
    }

    const customerName = cart[0].customerName || '—';
    const phone = cart[0].phone || '—';
    const address = cart[0].address || '';
    const smsConsent = cart[0].smsConsent || false;

    // --- Step 1: Submit to API to get an order ID ---
    // NOTE: previewThumbnail is still sent to the server for reference,
    // but is NOT included in the printed receipt HTML.
    let orderId = null;
    try {
      let BOOTH_API_URL = DREAMMEE_DEPLOY.boothApiUrl;
      // Automatically fallback to local server when testing on localhost
      if (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) {
        BOOTH_API_URL = window.location.origin + '/api/orders';
      }
      const BOOTH_API_KEY = DREAMMEE_DEPLOY.boothApiKey;

      const apiItems = cart.map(item => ({
        designId: item.designId || '',
        recipe: item.recipe,
        product: { ...item.product },
        customerNotes: item.customerNotes || '',
        previewThumbnail: item.previewThumbnail || ''
      }));

      const response = await fetch(BOOTH_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Api-Key': BOOTH_API_KEY },
        body: JSON.stringify({ customerName, phone, address, smsConsent, items: apiItems })
      });

      if (response.ok) {
        const data = await response.json();
        orderId = data.id;
        console.log('[DreamMee] Multi-item order created:', data);
      } else {
        console.warn('[DreamMee] API returned non-OK, printing without order ID');
      }
    } catch (err) {
      console.warn('[DreamMee] API submission failed, printing without order ID:', err.message);
    }

    // --- Step 2: Build receipt HTML (no preview images) ---
    const receiptHtml = this._buildReceiptHtml({ cart, orderId, customerName, phone });

    // --- Step 3: Dispatch to printer ---
    if (this._isIPadOS()) {
      // iPad detected → send to Star PassPRNT app via URL scheme
      console.log('[DreamMee] iPad detected — printing via Star PassPRNT app');
      this._printViaPassPRNT(receiptHtml);

      if (orderId) {
        this.showToast(`Order ${orderId} submitted! Opening printer app...`);
      }

      // Reset after a short delay (PassPRNT will redirect back via the back URL)
      setTimeout(() => {
        this._resetForNextCustomer();
      }, 3000);
    } else {
      // Desktop/other → use existing window.open + window.print flow
      console.log('[DreamMee] Non-iPad — printing via browser print dialog');
      const w = window.open('', '_blank', 'width=420,height=700');
      if (!w) {
        this.showToast('Pop-up blocked! Please allow pop-ups for receipt printing.');
        return;
      }

      // Inject auto-print script into the receipt HTML for the popup window
      const printableHtml = receiptHtml.replace(
        '</body>',
        `<script>
    window.onload = function() {
      setTimeout(function() { window.print(); }, 300);
    };
    window.onafterprint = function() {
      if (window.opener && window.opener.__dreammeeResetAfterPrint) {
        window.opener.__dreammeeResetAfterPrint();
      }
    };
  <\\/script>\n</body>`
      );

      w.document.write(printableHtml);
      w.document.close();

      if (orderId) {
        this.showToast(`Order ${orderId} submitted!`);
      }

      // Register callback for the popup's afterprint event
      const self = this;
      window.__dreammeeResetAfterPrint = function () {
        delete window.__dreammeeResetAfterPrint;
        self._resetForNextCustomer();
      };

      // Fallback: poll for popup closed (in case afterprint doesn't fire, e.g. user closes popup)
      const pollClosed = setInterval(() => {
        if (w.closed) {
          clearInterval(pollClosed);
          if (window.__dreammeeResetAfterPrint) {
            // afterprint never fired, but popup is gone — reset anyway
            window.__dreammeeResetAfterPrint();
          }
        }
      }, 500);
    }
  },

  /** Full reset for next customer — called after print or exit flow */
  _resetForNextCustomer() {
    document.getElementById('cart-viewer-overlay').style.display = 'none';
    document.getElementById('post-add-overlay').style.display = 'none';
    this.clearConventionCart();
    this.state.orderMode = null;
    this.state.designId = null;
    this.state.sceneRecipe = null;
    this.slotState = {};
    if (this.sceneComposer) {
      this.sceneComposer.stage.destroy();
      this.sceneComposer = null;
      const sc = document.getElementById('scene-canvas-container');
      if (sc) sc.innerHTML = '';
    }
    this._sceneDesignId = null;
    this._sceneModeKey = null;
    const fields = ['checkout-fullname', 'checkout-phone', 'checkout-address'];
    fields.forEach(id => { const el = document.getElementById(id); if (el) el.value = ''; });
    const notesInput = document.getElementById('modal-notes-input');
    if (notesInput) { notesInput.value = ''; }
    const notesCount = document.getElementById('modal-notes-count');
    if (notesCount) { notesCount.textContent = '0'; }
    this.goToStep(0);
  },

  // ==================== BOOTH ORDER SUBMISSION ====================

  async submitBoothOrder() {
    const nameInput = document.getElementById('booth-customer-name');
    const phoneInput = document.getElementById('booth-phone');
    const btn = document.getElementById('modal-booth-submit');
    const statusEl = document.getElementById('modal-status');
    const statusText = document.getElementById('modal-status-text');

    const customerName = (nameInput.value || '').trim();
    const phone = (phoneInput.value || '').trim();

    if (!customerName) {
      nameInput.focus();
      nameInput.classList.add('modal__booth-input--error');
      this.showToast('Please enter the customer name');
      return;
    }
    nameInput.classList.remove('modal__booth-input--error');

    if (!phone) {
      phoneInput.focus();
      phoneInput.classList.add('modal__booth-input--error');
      this.showToast('Please enter a phone number');
      return;
    }
    phoneInput.classList.remove('modal__booth-input--error');

    const customerNotes = this.sanitizeNotes(
      (document.getElementById('modal-notes-input') || {}).value || ''
    );
    const recipe = this.buildFullRecipe();
    if (customerNotes) { recipe.customerNotes = customerNotes; } else { delete recipe.customerNotes; }

    btn.disabled = true;
    btn.textContent = '⏳ Submitting...';

    const BOOTH_API_URL = DREAMMEE_DEPLOY.boothApiUrl;
    const BOOTH_API_KEY = DREAMMEE_DEPLOY.boothApiKey;

    try {
      const response = await fetch(BOOTH_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Api-Key': BOOTH_API_KEY },
        body: JSON.stringify({
          customerName, phone, recipe,
          product: {
            size: this.state.product?.size || '—',
            color: this.state.product?.color || '',
            colorName: this.state.product?.colorName || '—',
            designId: this.state.designId || '—'
          },
          customerNotes
        })
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || `HTTP ${response.status}`);
      }

      const data = await response.json();
      console.log('[DreamMee] Booth order created:', data);

      this.printBoothReceipt({
        orderId: data.id, customerName, phone,
        type: this.state.product?.type || 'tshirt',
        size: this.state.product?.size || '—',
        colorName: this.state.product?.colorName || '—',
        designId: this.state.designId || '—',
        customerNotes, previewSrc: this._previewThumbnail || ''
      });

      btn.textContent = '✅ Order Submitted!';
      statusEl.style.display = 'block';
      statusText.innerHTML = `<span style="color:var(--dm-success);">✅ Order <strong>${data.id}</strong> created!</span><br><span style="font-size:0.8rem;color:var(--dm-text-muted);">Receipt is printing.</span>`;
      this.showToast(`Order ${data.id} submitted!`);

      setTimeout(() => {
        btn.textContent = '🖨️ Print Receipt & Submit Order';
        btn.disabled = false;
        nameInput.value = '';
        phoneInput.value = '';
      }, 5000);

    } catch (err) {
      console.error('[DreamMee] Booth order error:', err);
      btn.textContent = '🖨️ Print Receipt & Submit Order';
      btn.disabled = false;
      statusEl.style.display = 'block';
      statusText.textContent = `Failed to submit order: ${err.message}`;
      statusText.style.color = 'var(--dm-error)';
    }
  },

  printBoothReceipt({ orderId, customerName, phone, type, size, colorName, designId, customerNotes, previewSrc }) {
    const now = new Date().toLocaleString();
    const w = window.open('', '_blank', 'width=420,height=700');
    if (!w) { this.showToast('Pop-up blocked!'); return; }

    const typeLabel = type === 'hoodie' ? 'Hoodie' : 'T-Shirt';

    // Build a single copy of the receipt body
    const receiptBody = `
<div class="header">★ DreamMee ★</div><div class="sub-header">Custom ${typeLabel} Order</div>
<div class="order-id">${orderId}</div><div class="divider"></div>
<div class="row"><span class="label">📞 Phone:</span><span class="value">${phone}</span></div>
<div class="row"><span class="label">👤 Name:</span><span class="value">${customerName}</span></div>
<div class="divider"></div>
<div class="row"><span class="label">Product:</span><span class="value">${typeLabel}</span></div>
<div class="row"><span class="label">Size:</span><span class="value">${size}</span></div>
<div class="row"><span class="label">Color:</span><span class="value">${colorName}</span></div>
<div class="row"><span class="label">Design:</span><span class="value">${designId}</span></div>
${customerNotes ? '<div class="divider"></div><div class="notes">📝 ' + customerNotes + '</div>' : ''}
${previewSrc ? '<div class="divider"></div><div class="preview"><img src="' + previewSrc + '"></div>' : ''}
<div class="divider"></div>
<div class="row"><span class="label">Time:</span><span class="value">${now}</span></div>
<div class="call-note">📞 CALL WHEN READY</div>
<div class="footer">DreamMee Booth — Thank you!</div>`;

    // Print two copies: one for shop, one for customer
    w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>DreamMee Receipt — ${orderId}</title>
<style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:'Courier New',monospace;width:80mm;margin:0 auto;padding:10px;color:#000;background:#fff}.header{text-align:center;font-size:20px;font-weight:bold;padding:10px 0 6px;border-bottom:3px double #000}.sub-header{text-align:center;font-size:11px;padding:4px 0 8px;color:#555}.order-id{text-align:center;font-size:32px;font-weight:bold;padding:12px 0;letter-spacing:3px;border:2px solid #000;margin:8px 0;background:#f5f5f5}.divider{border-top:1px dashed #000;margin:8px 0}.row{display:flex;justify-content:space-between;padding:4px 0;font-size:13px}.row .label{font-weight:bold}.row .value{text-align:right;max-width:60%;word-break:break-word}.notes{font-size:11px;padding:6px;background:#f5f5f5;margin:6px 0;white-space:pre-wrap;word-break:break-word;border:1px solid #ddd}.preview{text-align:center;padding:8px 0}.preview img{max-width:55mm;height:auto;border:1px solid #ccc}.call-note{text-align:center;font-size:16px;font-weight:bold;padding:10px;background:#000;color:#fff;margin:10px 0 6px}.footer{text-align:center;font-size:10px;padding:6px 0;border-top:3px double #000;color:#666}.copy-label{text-align:center;font-size:12px;font-weight:bold;padding:4px 0;text-transform:uppercase;letter-spacing:1px}.cut-line{border-top:3px dashed #000;margin:16px 0;text-align:center;position:relative}.cut-line::after{content:'✂ CUT HERE';position:relative;top:-10px;background:#fff;padding:0 8px;font-size:10px;font-weight:bold;letter-spacing:1px}@media print{body{width:80mm}@page{size:80mm auto;margin:0}}</style>
</head><body>
<div class="copy-label">— SHOP COPY —</div>
${receiptBody}
<div class="cut-line"></div>
<div class="copy-label">— CUSTOMER COPY —</div>
${receiptBody}
<script>window.onload=function(){setTimeout(function(){window.print();},300)};<\/script>
</body></html>`);
    w.document.close();
  },

  getShopifyVariantId() {
    const formInput = document.querySelector('#dreammee-form [name="id"]');
    if (formInput) return formInput.value;
    const container = document.querySelector('[data-variant-id]');
    if (container) return container.dataset.variantId;
    const params = new URLSearchParams(window.location.search);
    const urlVariant = params.get('variant');
    if (urlVariant) return urlVariant;
    return null;
  }
});
Object.assign(DreamMeeApp.prototype, {
  // ==================== THUMBNAIL GENERATION ====================

  /**
   * Generate a small JPEG thumbnail directly from a Konva Stage.
   * Uses toCanvas() to get a native canvas, avoiding data-URL-to-Image
   * decode timing issues that can produce blank thumbnails.
   * @param {Konva.Stage} stage - The Konva stage to capture
   * @param {number} thumbW - Target thumbnail width (px)
   * @param {number} thumbH - Target thumbnail height (px)
   * @param {number} quality - JPEG quality (0–1)
   * @returns {string} JPEG data URL (~10-25 KB)
   */
  generateThumbnailFromStage(stage, thumbW, thumbH, quality) {
    try {
      // Get a native canvas at 1:1 from the Konva stage
      const sourceCanvas = stage.toCanvas({ pixelRatio: 1 });

      // Draw onto a small thumbnail canvas
      const canvas = document.createElement('canvas');
      canvas.width = thumbW;
      canvas.height = thumbH;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(sourceCanvas, 0, 0, thumbW, thumbH);

      const jpegDataUrl = canvas.toDataURL('image/jpeg', quality);
      const sizeKB = Math.round((jpegDataUrl.length * 3) / 4 / 1024);
      console.log(`[DreamMee] Thumbnail generated: ${thumbW}×${thumbH}, ~${sizeKB} KB`);
      return jpegDataUrl;
    } catch (err) {
      console.warn('[DreamMee] Thumbnail generation failed:', err);
      return null;
    }
  },

  // ==================== NOTES SANITIZATION ====================

  /**
   * Sanitize customer notes: strip special characters, keep only safe text.
   * Allows letters (any language), numbers, spaces, basic punctuation.
   * Enforces a 1000-character maximum.
   * @param {string} raw - Raw user input
   * @returns {string} Sanitized string
   */
  sanitizeNotes(raw) {
    if (!raw) return '';
    // Allow word characters (Unicode-aware), spaces, and basic punctuation: . , ! ? ' " - ( ) / : ; @ # &
    // Strip everything else (angle brackets, backticks, pipe, etc.)
    return raw
      .replace(/[^\p{L}\p{N}\s.,!?'"\-()/:;@#&]/gu, '')
      .slice(0, 1000);
  },

  // ==================== TOAST ====================

  showToast(message) {
    document.querySelectorAll('.toast').forEach(t => t.remove());
    const toast = document.createElement('div');
    toast.className = 'toast';
    toast.textContent = message;
    document.body.appendChild(toast);
    requestAnimationFrame(() => toast.classList.add('toast--show'));
    setTimeout(() => {
      toast.classList.remove('toast--show');
      setTimeout(() => toast.remove(), 350);
    }, 2500);
  },

  // ==================== SCENE HINT ====================

  showSceneHint() {
    const hint = document.getElementById('scene-hint');
    if (!hint) return;

    // Show the hint with animation
    hint.classList.remove('scene-hint--hidden');
    hint.classList.add('scene-hint--visible');

    // Dismiss function
    const dismiss = () => {
      hint.classList.remove('scene-hint--visible');
      hint.classList.add('scene-hint--hidden');
    };

    // Auto-dismiss after 10 seconds
    const timer = setTimeout(dismiss, 10000);

    // Dismiss immediately on first interaction with the canvas
    const wrapper = document.getElementById('scene-canvas-wrapper');
    const onInteract = () => {
      clearTimeout(timer);
      dismiss();
      wrapper.removeEventListener('pointerdown', onInteract);
    };
    wrapper.addEventListener('pointerdown', onInteract, { once: true });

    // Auto-select a node to show transformer handles as a visual cue
    // Poll because rendering custom assets (like generating builder data URLs) can take variable time
    if (this.sceneComposer) {
      let attempts = 0;
      const trySelect = () => {
        const sc = this.sceneComposer;
        const state = sc.state || {};

        let isReady = false;
        let target = null;

        // Determine the ideal target, waiting for it to render
        if (state.showCharacter !== false) {
          if (sc.characterNode) { isReady = true; target = sc.characterNode; }
        } else if (state.showCar !== false) {
          if (sc.carNode) { isReady = true; target = sc.carNode; }
        } else if (state.showText !== false) {
          if (sc.textNode) { isReady = true; target = sc.textNode; }
        }

        if (isReady && target && sc.transformer) {
          sc.transformer.nodes([target]);
          sc.transformerLayer.batchDraw();
        } else if (attempts < 15) {
          attempts++;
          setTimeout(trySelect, 200);
        } else {
          // Fallback if the expected target never rendered
          const fallback = sc.characterNode || sc.carNode || sc.textNode;
          if (fallback && sc.transformer) {
            sc.transformer.nodes([fallback]);
            sc.transformerLayer.batchDraw();
          }
        }
      };
      setTimeout(trySelect, 300);
    }
  }
});

// ==================== PREVIEW SHIRT SELECTOR WIDGET ====================
class PreviewShirtSelector {
  constructor(app, container, idSuffix) {
    this.app = app;
    this.container = typeof container === 'string' ? document.querySelector(container) : container;
    this.idSuffix = idSuffix;
    this.init();
  }

  init() {
    if (!this.container) return;

    const widget = document.createElement('div');
    widget.className = 'preview-shirt-selector';

    const sizes = DreamMeeApp.SHIRT_SIZES;
    const colors = DreamMeeApp.SHIRT_COLORS;

    widget.innerHTML = `
      <div class="preview-select-wrap">
        <select class="preview-select" id="preview-type-select-${this.idSuffix}">
          <option value="tshirt">Product: T-Shirt</option>
          <option value="hoodie">Product: Hoodie</option>
        </select>
        <span class="preview-select-arrow"></span>
      </div>
      <div class="preview-select-wrap">
        <select class="preview-select" id="preview-size-select-${this.idSuffix}">
          ${sizes.map(s => `<option value="${s}">Size: ${s}</option>`).join('')}
        </select>
        <span class="preview-select-arrow"></span>
      </div>
      <div class="preview-select-wrap">
        <select class="preview-select" id="preview-color-select-${this.idSuffix}">
          ${colors.map(c => `<option value="${c.hex}" data-color-name="${c.name}">Color: ${c.name}</option>`).join('')}
        </select>
        <span class="preview-select-arrow"></span>
      </div>
    `;

    this.container.appendChild(widget);

    const typeSelect = widget.querySelector(`#preview-type-select-${this.idSuffix}`);
    const sizeSelect = widget.querySelector(`#preview-size-select-${this.idSuffix}`);
    const colorSelect = widget.querySelector(`#preview-color-select-${this.idSuffix}`);

    // Set initial values
    if (typeSelect) typeSelect.value = this.app.state.product.type || 'tshirt';
    if (sizeSelect) sizeSelect.value = this.app.state.product.size;
    if (colorSelect) colorSelect.value = this.app.state.product.color;

    // Bind change events
    if (typeSelect) {
      typeSelect.addEventListener('change', (e) => {
        this.app.state.product.type = e.target.value;
        this.app.syncShirtOptions();
        this.app._populateProductStep();
      });
    }

    sizeSelect.addEventListener('change', (e) => {
      this.app.state.product.size = e.target.value;
      this.app.syncShirtOptions();
    });

    colorSelect.addEventListener('change', (e) => {
      const hex = e.target.value;
      const option = colorSelect.options[colorSelect.selectedIndex];
      const name = option.getAttribute('data-color-name');
      this.app.state.product.color = hex;
      this.app.state.product.colorName = name;
      this.app.syncShirtOptions();
    });
  }
}

// ==================== BOOT ====================
document.addEventListener('DOMContentLoaded', () => {
  // Merge new character poses before app init
  if (typeof mergeNewPoses === 'function') mergeNewPoses();
  window.dreamMeeApp = new DreamMeeApp('dreammee-app');
});
